<template>
  <section id="all-orders-listing" class="all-cases-view">
    <div class="container-default">
      <div class="listings-navbar">
        <el-row gutter="20" style="row-gap: 5px">
          <el-col :lg="10" :xl="10" class="toolbar-left">
            <ul class="d-flex list-items-group-15 barcode-print">
              <li>
                <el-button class="btn button-with-icon" :disabled="!selectedCaseIds.length" @click="onBulkAccession">
                  <span>BULK ACCESSION</span>
                </el-button>
              </li>
              <li v-if="hasPermission('generate_order_barcode', 'view')">
                <el-button
                  class="btn button-with-icon generate-barcode-btn"
                  :disabled="!selectedCaseIds.length"
                  @click="onPrintBarCodes"
                >
                  <span class="mr-8">
                  </span>
                  <span>GENERATE BARCODE</span>
                </el-button>
              </li>
              <li>
                <div class="form-element input-with-icon search-input-lg has-search-shortcut is-compact">
                  <el-input
                    ref="orderInput"
                    placeholder="Order Id / Patient Name / Accession Id"
                    v-model="search_string"
                    @input="getAutoCompleteOrders()"
                    :clearable="true"
                  ></el-input>
                  <span v-if="!search_string" class="search-shortcut-hint"><kbd v-for="key in searchShortcutKeys" :key="key">{{ key }}</kbd></span>
                </div>
              </li>
              <li v-if="hasPermission('cases', 'create_case')">
                <div class="form-element input-with-icon search-input-lg scan-qr-input">
                  <el-input
                    ref="barcodeInput"
                    placeholder="SCAN QR CODE"
                    v-model="barcodeNumber"
                    @input="getOrderDetails()"
                    :clearable="true"
                  ></el-input>
                  <span>
                    <img
                      src="@/assets/images/icons/search-icon-with-bg-red.svg"
                      alt="icon"
                      class="img-fluid icon"
                    />
                  </span>
                </div>
              </li>
            </ul>
          </el-col>
          <el-col :lg="14" :xl="14" class="toolbar-right">
            <ul class="d-flex justify-content-end list-items-group-15">
              <li>
                <div class="search-with-date-input">
                  <el-date-picker
                    v-model="date_of_birth"
                    format="MM-DD-YYYY"
                    :disabled-date="disabledDate"
                    @change="fetchAllOrders()"
                    placeholder="DOB"
                    :clearable="true"
                  ></el-date-picker>
                </div>
              </li>
              <li>
                <div class="search-with-date-input">
                  <el-date-picker
                    v-model="fromDate"
                    type="date"
                    :placeholder="fromDatePlaceholder"
                    format="MM-DD-YYYY"
                    :clearable="true"
                    :disabled-date="disabledFromDate"
                    @change="onFromDateChange"
                    value-format="YYYY-MM-DD"
                    style="width: 100%"
                  />
                </div>
              </li>
              <li>
                <div class="search-with-date-input">
                  <el-date-picker
                    v-model="toDate"
                    type="date"
                    :placeholder="toDatePlaceholder"
                    format="MM-DD-YYYY"
                    :clearable="true"
                    value-format="YYYY-MM-DD"
                    :disabled-date="disabledToDate"
                    style="width: 100%"
                    @change="handleCurrentChange(1)"
                  />
                </div>
              </li>
              <li v-if="!showFilters">
                <el-button
                  class="btn button-with-icon"
                  @click="onReportsExport"
                >
                  <span class="mr-8">
                    <img
                      src="@/assets/images/icons/export-icon-with-bg-blue.svg"
                      alt="icon"
                      class="inactive"
                    />
                    <img
                      src="@/assets/images/icons/export-icon-white.svg"
                      alt="icon"
                      class="active"
                    />
                  </span>
                  <span>Export</span>
                </el-button>
              </li>
              <li>
                <el-button
                  class="filter-toggle-btn"
                  @click="showFilters = !showFilters"
                >
                  {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
                </el-button>
              </li>
              <li>
                <el-button class="filter-clear-btn" @click="clearAllFilters">
                  Clear Filters
                </el-button>
              </li>
              <li v-if="showFilters">
                <el-popover
                  placement="bottom-end"
                  :width="250"
                  trigger="click"
                  popper-class="filter-customize-popover"
                >
                  <template #reference>
                    <el-button class="filter-customize-btn" title="Customize filters">
                      Customize Filters
                    </el-button>
                  </template>
                  <div class="filter-customize-panel">
                    <p class="fc-title">Date range applies to</p>
                    <div class="fc-date-type">
                      <el-select
                        v-model="dateType"
                        placeholder="Date Type"
                        default-first-option
                        @change="onDateTypeChange"
                      >
                        <el-option
                          v-for="opt in dateTypeOptions"
                          :key="opt.value"
                          :label="opt.label"
                          :value="opt.value"
                        />
                      </el-select>
                    </div>
                    <p class="fc-title">Show / hide &amp; drag to reorder</p>
                    <div
                      v-for="f in customizableFilters"
                      :key="f.key"
                      class="fc-row"
                    >
                      <el-checkbox
                        :model-value="!hiddenFilters.includes(f.key)"
                        @change="toggleFilterVisibility(f.key)"
                      >
                        {{ f.label }}
                      </el-checkbox>
                    </div>
                    <el-button
                      text
                      size="small"
                      class="fc-reset"
                      @click="resetFilterPreferences"
                    >
                      Reset to default
                    </el-button>
                  </div>
                </el-popover>
              </li>
            </ul>
          </el-col>
        </el-row>
        <transition name="slide-filters">
          <el-row v-if="showFilters" class="mt-10">
            <el-col :span="24">
              <draggable
                v-model="orderedVisibleFilters"
                item-key="key"
                tag="ul"
                class="d-flex list-items-group-15 filters-draggable"
                handle=".filter-drag-handle"
                ghost-class="filter-ghost"
              >
                <template #item="{ element }">
                  <li class="filter-drag-item">
                    <span class="filter-drag-handle" title="Drag to reorder">⠿</span>
                    <template v-if="element.key === 'ordering_facility'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="selectedHospital"
                          placeholder="Ordering Facility"
                          filterable
                          clearable
                          multiple
                          collapse-tags
                          collapse-tags-tooltip
                          :max-collapse-tags="1"
                          :reserve-keyword="false"
                          default-first-option
                          value-key="_id"
                          @change="onHospitalChange"
                          remote
                          @focus="onHospitalFocus"
                          :remote-method="remoteMethodFacility"
                          :loading="hospitalsLoading"
                        >
                          <el-option
                            v-for="hospital in hospitalsData.data"
                            :key="hospital"
                            :label="hospital.name"
                            :value="hospital"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'physician'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="physician"
                          placeholder="Physician"
                          filterable
                          clearable
                          multiple
                          collapse-tags
                          collapse-tags-tooltip
                          :max-collapse-tags="1"
                          :reserve-keyword="false"
                          default-first-option
                          @change="handleCurrentChange(1)"
                          @focus="onPhysicianFocus"
                          :loading="physiciansLoading"
                          remote
                          :remote-method="remoteMethodPhysician"
                        >
                          <el-option
                            v-for="physician in physiciansData.data"
                            :key="physician"
                            :label="physician.name"
                            :value="physician._id"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'case_type'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="caseType"
                          placeholder="Case Type"
                          filterable
                          clearable
                          multiple
                          collapse-tags
                          collapse-tags-tooltip
                          :max-collapse-tags="1"
                          default-first-option
                          :reserve-keyword="false"
                          @change="handleCurrentChangeCaseType(1)"
                        >
                          <el-option
                            v-for="caseType in getAuthenticatedUser?.user_type === 'LAB_TECHNOLOGIST' && getAuthenticatedUser?.allowed_case_types ? getUserBasedCaseTypes(caseTypes) : getLabBasedCaseTypes(caseTypes)"
                            :key="caseType"
                            :label="caseType.label"
                            :value="caseType.value"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'sales_rep'">
                      <div class="form-element search-with-filter-input marketers">
                        <el-select
                          v-model="hospital_marketer"
                          placeholder="Sales Rep"
                          default-first-option
                          :reserve-keyword="false"
                          filterable
                          clearable
                          @change="handleCurrentChange(1)"
                          @focus="fetchAllUsers"
                          :loading="userLoading"
                          multiple
                          collapse-tags
                        >
                          <el-option
                            v-for="(user, index) in marketerData"
                            :key="index"
                            :label="user.first_name + ' ' + user.last_name"
                            :value="user._id"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'priority'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="priority"
                          placeholder="Priority"
                          filterable
                          :clearable="true"
                          default-first-option
                          @clear="clearTest"
                          :reserve-keyword="false"
                          @change="handleCurrentChange(1)"
                        >
                          <el-option
                            v-for="priority in orderPriorityTypes"
                            :key="priority"
                            :label="priority.name"
                            :value="priority.value"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'platform'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="testgo_order_type"
                          placeholder="Platform"
                          default-first-option
                          :reserve-keyword="false"
                          filterable
                          clearable
                          multiple
                          collapse-tags
                          collapse-tags-tooltip
                          :max-collapse-tags="1"
                          @change="handleCurrentChange(1)"
                        >
                          <el-option
                            v-for="platform in this.platformCategoriesMap"
                            :key="platform"
                            :label="platform"
                            :value="platform"
                          >
                          </el-option>
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'technician_name'">
                      <div class="form-element search-with-filter-input marketers">
                        <el-select
                          v-model="technician_name"
                          placeholder="Technician Name"
                          filterable
                          clearable
                          multiple
                          collapse-tags
                          collapse-tags-tooltip
                          :max-collapse-tags="1"
                          default-first-option
                          :reserve-keyword="false"
                          @change="handleCurrentChange(1)"
                          @focus="fetchAllTechnicians"
                          :loading="technicianLoading"
                        >
                          <el-option
                            v-for="(name, index) in technicianData"
                            :key="index"
                            :label="name"
                            :value="name"
                          />
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'acknowledgement'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="acknowledgementFilter"
                          placeholder="Acknowledgement"
                          clearable
                          @change="handleCurrentChange(1)"
                        >
                          <el-option label="YES" value="YES" />
                          <el-option label="NO" value="NO" />
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'partial_only'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="showPartialOnly"
                          placeholder="Partial"
                          clearable
                          @change="handleCurrentChange(1)"
                        >
                          <el-option label="PARTIAL" :value="true" />
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'partial_reason'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="partialReasonFilter"
                          placeholder="Partial Reason"
                          filterable
                          clearable
                          multiple
                          collapse-tags
                          collapse-tags-tooltip
                          :max-collapse-tags="1"
                          default-first-option
                          :reserve-keyword="false"
                          @change="handleCurrentChange(1)"
                        >
                          <el-option
                            v-for="option in partialReasonOptions"
                            :key="option.value"
                            :label="option.label"
                            :value="option.value"
                          />
                        </el-select>
                      </div>
                    </template>
                    <template v-else-if="element.key === 'sent_to_mlx'">
                      <div class="form-element search-with-filter-input">
                        <el-select
                          v-model="sentToMlxFilter"
                          placeholder="Sent to MLX"
                          clearable
                          @change="handleCurrentChange(1)"
                        >
                          <el-option label="YES" value="YES" />
                          <el-option label="NO" value="NO" />
                        </el-select>
                      </div>
                    </template>
                  </li>
                </template>
              </draggable>
            </el-col>
          </el-row>
        </transition>
      </div>
    </div>

    <div class="container-default" style="padding-top: 0">
      <!-- Web Version -->
      <div class="primary-content-view-new" v-loading="loading">
        <div class="listings listings__table listings__all-cases">
          <el-table
            :data="displayedOrders"
            height="calc(100vh - 200px)"
            :default-sort="{
              prop: orderBy,
              order: orderType === 'asc' ? 'ascending' : 'descending',
            }"
            @sort-change="onChangeSort"
            @selection-change="handleSelectionChange($event)"
            row-key="_id"
            :lazy="false"
            :row-class-name="({ row }) => hasPayorMapMatch(row) ? 'payor-mapped-row' : ''"
          >
            <el-table-column
              type="selection"
              width="40"
              class="selection-check"
              :selectable="isOrderAccessionable"
            ></el-table-column>
            <el-table-column
              label="ORDER ID"
              prop="order_id"
              width="180"
              sortable="custom"
              fixed="left"
            >
              <template #default="scope">
                <div id="name-accession">
                  <span class="order-id-wrap ml-5">
                    <span
                      id="accession-id"
                      @click="goToOrdersView(scope.row._id)"
                      class="cursor"
                    >
                      {{ scope.row.order_id }}
                    </span>
                    <span
                      v-if="scope.row.order_type"
                      class="platform-corner-badge"
                      :class="'platform-' + String(scope.row.order_type).toLowerCase()"
                    >
                      {{ scope.row.order_type }}
                    </span>
                  </span>
                  <span
                    v-if="scope.row.is_stat && !showStatCase()"
                    class="stat-badge"
                  >
                    STAT
                  </span>
                  <el-tooltip
                    v-if="orderAccessionDisabledReason(scope.row)"
                    :content="orderAccessionDisabledReason(scope.row)"
                    placement="top"
                  >
                    <el-icon class="ml-5" style="color: #e6a23c; cursor: help; vertical-align: middle;">
                      <WarningFilled />
                    </el-icon>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
             <el-table-column
              label="COLLECTED TUBES"
              min-width="200"
              fixed="left"
            >
              <template #default="scope">
                <div
                  v-if="
                    scope.row.collected_tubes &&
                    scope.row.collected_tubes.length
                  "
                >
                  <el-tooltip effect="dark" placement="bottom">
                    <template #content>
                      <div style="display: flex; flex-wrap: wrap; gap: 4px">
                        <span
                          v-for="(tube, ti) in scope.row.collected_tubes"
                          :key="ti"
                          style="
                            background: #fff;
                            color: #333;
                            padding: 2px 8px;
                            border-radius: 4px;
                            font-size: 12px;
                          "
                        >
                          {{ tube.tube_name }} x{{ tube.tube_count }}
                        </span>
                      </div>
                    </template>
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        cursor: pointer;
                        padding: 5px;
                        flex-wrap: nowrap;
                      "
                    >
                      <span
                        v-for="(tube, ti) in scope.row.collected_tubes.slice(
                          0,
                          2,
                        )"
                        :key="ti"
                        class="tube-badge"
                        >{{ tube.tube_name }} x{{ tube.tube_count }}</span
                      >
                      <span
                        v-if="scope.row.collected_tubes.length > 2"
                        class="tube-badge"
                        style="background: #e0e0e0; color: #555"
                        >+{{ scope.row.collected_tubes.length - 2 }}</span
                      >
                    </div>
                  </el-tooltip>
                </div>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column
              v-if="showConversionColumn"
              label="CONVERSION HELPER"
              min-width="200"
              fixed="left"
            >
              <template #default="scope">
                <div class="conversion-guidance">
                  <el-tag v-if="scope.row.insurance_on_hold" size="small" type="danger" effect="dark">
                    INSURANCE ON HOLD
                  </el-tag>
                  <el-tooltip
                    v-for="entry in getConversionGuidance(scope.row)"
                    :key="entry.case_type"
                    placement="right"
                    effect="dark"
                  >
                    <template #content>
                      <div class="conversion-guidance-tip">
                        <div class="tip-title">
                          {{ caseTypeMap[entry.case_type] || entry.case_type }} at {{ shortLabName(entry.lab_name) }}
                        </div>
                        <div v-if="entry.redirect_lab_name">Registers at {{ shortLabName(entry.redirect_lab_name) }}</div>
                        <div v-if="entry.earliest_received_date">
                          Received date on or after {{ formatGuidanceDate(entry.earliest_received_date) }}
                        </div>
                        <div v-for="(message, mi) in entry.messages" :key="mi">• {{ message }}</div>
                        <div v-if="entry.status === 'OK' && !entry.messages.length">No conversion restrictions.</div>
                        <div v-if="getOtherLabGuidance(scope.row, entry).length" class="tip-other-labs">
                          <div class="tip-subtitle">Other labs</div>
                          <div v-for="other in getOtherLabGuidance(scope.row, entry)" :key="other.lab">
                            {{ shortLabName(other.lab_name) }}: {{ conversionStatusLabel[other.status] }}
                            <template v-if="other.status === 'REDIRECT'">
                              (registers at {{ shortLabName(other.redirect_lab_name) }})
                            </template>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div class="conversion-guidance-row">
                      <el-icon class="conversion-status-icon" :class="'status-' + entry.status.toLowerCase()">
                        <CircleCheckFilled v-if="entry.status === 'OK'" />
                        <Right v-else-if="entry.status === 'REDIRECT'" />
                        <CircleCloseFilled v-else />
                      </el-icon>
                      <span v-if="getConversionGuidance(scope.row).length > 1" class="conversion-case-type">
                        {{ caseTypeMap[entry.case_type] || entry.case_type }}
                      </span>
                      <span class="conversion-guidance-text" :class="'status-' + entry.status.toLowerCase()">
                        {{ conversionHelperText(scope.row, entry) }}
                      </span>
                    </div>
                  </el-tooltip>
                  <span v-if="!scope.row.insurance_on_hold && !getConversionGuidance(scope.row).length">--</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Case Type"
              width="135"
              prop="case_types"
              fixed="left"
              sortable="custom"
            >
              <template #default="scope">
                <div
                  class="case-type"
                  v-if="scope.row.case_types && scope.row.case_types.length > 1"
                >
                  <el-dropdown>
                    <span class="case-data-type">
                      {{ getFormattedCaseTypes(scope.row)[0] }}

                      <img
                        src="@/assets/images/case-type-arrow.svg"
                        alt="icon"
                      />
                    </span>

                    <template #dropdown>
                      <el-dropdown-menu class="case-type-dropdown">
                        <el-dropdown-item
                          class="cases-view"
                          v-for="(caseType, index) of getFormattedCaseTypes(
                            scope.row
                          )"
                          :key="index"
                        >
                          {{ caseType }}
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
                <div v-else class="case-type">
                  <span class="case-data-type">
                    {{ getFormattedCaseTypes(scope.row)[0] }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="PATIENT NAME"
              prop="patient_info.first_name"
              min-width="160"
              sortable="custom"
              fixed="left"
            >
              <template #default="scope">
                <el-button
                  placement="top"
                  class="title-on-hover"
                  :title="getFormattedPatientName(scope.row)"
                  @click="copyTitle(getFormattedPatientName(scope.row))"
                >
                  <span class="text-center ordering_facility-value">{{
                    getFormattedPatientName(scope.row)
                  }}</span>
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label="Patient DOB"
              prop="date_of_birth"
              min-width="100"
            >
              <template #default="scope">
                <div style="cursor: default">
                  {{ formatDate(scope.row.patient_info.date_of_birth) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="hospitals"
              label="ORDERING FACILITIES"
              min-width="200"
            >
              <template #default="scope">
                <el-button
                  placement="top"
                  class="title-on-hover"
                  :title="scope.row.actual_hospital_name || (scope.row.hospital ? scope.row.hospital.name : '--')"
                >
                  <span class="text-center ordering_facility-value">{{
                    scope.row.hospital ? scope.row.hospital.name : "--"
                  }}</span>
                </el-button>
              </template>
            </el-table-column>
            <el-table-column prop="physican" label="physician" min-width="150">
              <template #default="scope">
                <el-button
                  placement="top"
                  class="title-on-hover"
                  :title="
                    /* scope.row.ordering_physician
                      ? scope.row.ordering_physician.name.includes('Dr')
                        ? scope.row.ordering_physician.name
                        : 'Dr ' + scope.row.ordering_physician.name
                      : '--' */
                    scope.row.ordering_physician
                      ? scope.row.ordering_physician.name
                      : '--'
                  "
                >
                  <span class="ordering_physician-value">{{
                    /* scope.row.ordering_physician
                      ? scope.row.ordering_physician.name.includes("Dr")
                        ? scope.row.ordering_physician.name
                        : "Dr " + scope.row.ordering_physician.name
                      : "--" */
                    scope.row.ordering_physician
                      ? scope.row.ordering_physician.name
                      : "--"
                  }}</span>
                </el-button>
              </template>
            </el-table-column>
            <el-table-column
              label="Order Placed ON"
              min-width="150"
              prop="created_at"
              sortable="custom"
            >
              <template #default="scope">
                <div v-if="scope.row.order_placed_at" class="d-flex">
                  <div style="cursor: default" class="date pr-10">
                    {{ momentWithTimezone(scope.row.order_placed_at, "MM-DD-yyyy") }}
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Collected On"
              min-width="130"
              prop="collection_date"
              sortable="custom"
            >
              <template #default="scope">
                <div v-if="scope.row.collection_date" class="d-flex">
                  <div style="cursor: default" class="date pr-10">
                    {{ momentWithTimezone(scope.row.collection_date, "MM-DD-yyyy") }}
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="ACKNOWLEDGED"
              min-width="150"
              prop="collected_order"
            >
              <template #default="scope">
                <div style="cursor: default">
                  {{ scope.row.collected_order ? "YES" : "NO" }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Status"
              min-width="140"
              prop="status"
              sortable="custom"
            >
              <template #default="scope">
                <div
                  class="column-status"
                  v-if="
                    hasPermission('orders', 'pending_rejection') &&
                    scope.row.status
                  "
                >
                  <div
                    v-if="
                      scope.row.status !== 'COMPLETED' &&
                      scope.row.status !== 'ARCHIVED'
                    "
                  >
                    <el-dropdown
                      class="status-dropdown"
                      :disabled="
                        scope.row.status === 'COMPLETED'
                      "
                      @command="
                        (command) =>
                          handleStatusChange(command, scope.row, scope.$index)
                      "
                      trigger="hover"
                      v-if="hasPermission('orders', 'update_status')"
                    >
                      <span class="el-dropdown-link">
                        <el-tag
                          :type="getStatusType(scope.row.status)"
                          style="cursor: pointer"
                        >
                          {{ formatStatusText(scope.row.status) }}
                        </el-tag>
                      </span>
                      <template #dropdown>
                        <el-dropdown-menu
                          class="status-dropdown-menu center-dropdown"
                        >
                          <div
                            v-if="scope.row.status === 'PARTIALLY_REJECTED'"
                            style="display: flex; align-items: center;"
                          >
                            <el-dropdown-item command="REJECTED">
                              REJECT
                            </el-dropdown-item>
                            <el-dropdown-item command="ACTIVE">
                              ACTIVE
                            </el-dropdown-item>
                          </div>
                          <span v-else-if="scope.row.status === 'REJECTED'">
                            <el-dropdown-item
                              command="ACTIVE"
                              :disabled="scope.row.status === 'ACTIVE'"
                            >
                              ACTIVE
                            </el-dropdown-item>
                          </span>
                          <el-dropdown-item
                            v-if="scope.row.status !== 'PARTIALLY_REJECTED'"
                            command="REJECTED"
                            :disabled="scope.row.status === 'REJECTED'"
                          >
                            REJECTED
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                  <div v-else>
                    <!-- Render content when status is 'FINALIZED' -->
                    <el-tag
                      :type="getStatusType(scope.row.status)"
                      style="cursor: not-allowed"
                    >
                      {{ formatStatusText(scope.row.status) }}
                    </el-tag>
                  </div>
                </div>
                <div v-else class="column-status">
                  <el-tag :type="getStatusType(scope.row.status)">
                    {{ formatStatusText(scope.row.status) }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Issue Reasons"
              min-width="200"
              v-if="issues === true"
            >
              <template #default="scope">
                <div class="name-accession cursor">
                  <span
                    id="accession-id-blue"
                    :title="
                      scope.row.requires_attention_reason
                        ? scope.row.requires_attention_reason
                        : '--'
                    "
                  >
                    {{
                      scope.row.requires_attention_reason
                        ? scope.row.requires_attention_reason
                        : "--"
                    }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="REJECTED REASONS"
              width="300"
              v-if="$route.path === '/all-orders/rejection'"
            >
              <template #default="{ row }">
                <div class="name-accession cursor">
                  <el-tooltip
                    v-if="row.notes && row.notes.length > 0"
                    effect="dark"
                    placement="top"
                  >
                    <template #content>
                      {{ row.notes.join(", ") }}
                    </template>
                    <span id="accession-id-blue" class="truncate-text">
                      {{ row.notes.join(", ") }}
                    </span>
                  </el-tooltip>
                  <span v-else>--</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="PARTIAL REASON" width="300" v-if="showPartialOnly === true">
              <template #default="{ row }">
                <div class="name-accession cursor">
                  <el-tooltip v-if="getPartialReason(row)" effect="dark" placement="top">
                    <template #content>
                      {{ getPartialReason(row) }}
                    </template>
                    <span id="accession-id-blue" class="truncate-text">
                      {{ getPartialReason(row) }}
                    </span>
                  </el-tooltip>
                  <span v-else>--</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="accession_id"
              label="ACCESSION ID"
              min-width="280"
              sortable="custom"
              width="280"
            >
              <template #default="scope">
                <div
                  class="name-accession cursor"
                  v-if="
                    scope.row.accession_data && scope.row.accession_data.length
                  "
                >
                  <span
                    id="accession-id-blue"
                    class="order-accession"
                    v-for="(acccession, index) in scope.row.accession_data"
                    :key="index"
                    @click="goToCaseView(acccession.case_id)"
                  >
                    {{ acccession.accession_id }}
                  </span>
                </div>
                <div
                  class="name-accession cursor"
                  @click="goToCaseView(scope.row.case_id)"
                  v-else-if="scope.row.case_id"
                >
                  <span id="accession-id-blue" class="order-accession">
                    {{ scope.row.accession_id }}
                  </span>
                </div>
              </template>
            </el-table-column>

            <!-- <el-table-column label="Phlebio Status" width="130" v-if="hasPermission('orders', 'create_accession')">
              <template #default="scope">
                <div class="column-status" v-if="scope.row.phlebio_order_id">
                  <span style="cursor: default;" class="el-dropdown-link">
                    {{
                    scope.row.phlebio_order_id
                    ? scope.row.phlebio_order_id.Status
                    : "--"
                    }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Phlebio Order Id" width="140" v-if="hasPermission('orders', 'create_accession')">
              <template #default="scope">
                <div
                  class="name-accession cursor"
                  v-if="scope.row.phlebio_order_id"
                  @click="viewPhlebioOrder(scope.row.phlebio_order_id._id)"
                >
                  <span id="accession-id-blue" class="order-accession">
                    {{ scope.row.phlebio_order_id.Phlebio_Order_id }}
                  </span>
                </div>
              </template>
            </el-table-column> -->
            <el-table-column
              label="ARCHIVE REASON"
              width="300"
              v-if="
                hasPermission('orders', 'view') &&
                this.$route.path === '/all-orders/archive'
              "
            >
              <template #default="scope">
                <div class="name-accession cursor">
                  <span id="accession-id-blue">
                    {{
                      scope.row.archive_reason ? scope.row.archive_reason : "--"
                    }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="action"
              label="Actions"
              width="150"
              fixed="right"
            >
              <template v-slot="scope">
                <ul class="listings__action-buttons">
                  <li @click="goToOrdersView(scope.row._id)">
                    <el-button class="view-button" title="View Order">
                      <img
                        class="action-active"
                        src="@/assets/images/icons/actions/view-icon.svg"
                        alt="icon"
                      />
                      <img
                        class="action-inactive"
                        src="@/assets/images/icons/actions/view-inactive.svg"
                        alt="icon"
                      />
                    </el-button>
                  </li>
                  <li v-if="isClientSalesUser">
                    <el-button
                      class="view-button priority-button"
                      :disabled="!!scope.row.is_priority"
                      :title="scope.row.is_priority ? 'Priority: ' + (scope.row.priority_reason || '') : 'Mark as Priority'"
                      @click="openPriorityDialog(scope.row)"
                    >
                      <el-icon :style="{ color: scope.row.is_priority ? '#e6a23c' : '#909399' }">
                        <StarFilled v-if="scope.row.is_priority" />
                        <Star v-else />
                      </el-icon>
                    </el-button>
                  </li>
                  <li v-if="hasPermission('orders', 'row_actions') && scope.row.sample_approval_data && scope.row.sample_approval_data.length > 0">
                    <el-tooltip
                      :content="'Use Previous Order Sample: ' + scope.row.sample_approval_data.map(s => s.order_id).join(', ')"
                      placement="top"
                    >
                      <img
                        style="height: 18px; width: 18px; cursor: default;"
                        src="@/assets/images/icons/actions/re-order-active.svg"
                        alt="previous-order-sample"
                      />
                    </el-tooltip>
                  </li>
                  <li
                    v-if="
                      hasPermission('orders', 'row_actions') &&
                      hasPermission('orders', 'delete') &&
                      scope.row.status != 'ARCHIVED' &&
                      scope.row.status == 'ACTIVE' && !this.validationStatus
                    "
                  >
                    <el-button
                      title="Archive Order"
                      class="delete-button"
                      @click="onDelete(scope.row._id)"
                    >
                      <img
                        class="action-active"
                        src="@/assets/images/icons/actions/archive-icon.svg"
                        alt="icon"
                      />
                      <img
                        class="action-inactive"
                        src="@/assets/images/icons/actions/archive-inactive.svg"
                        alt="icon"
                      />
                    </el-button>
                  </li>
                  <li v-if="hasPermission('orders', 'row_actions') && hasPermission('orders', 'archive_orders') && !this.validationStatus">
                    <el-button
                      title="Move To Validation"
                      class="reports-button validation-button"
                      @click="moveToValidations(scope.row._id)"
                    >
                      <img
                        class="action-active"
                        src="@/assets/images/icons/actions/validation-new-active.svg"
                        alt="icon"
                      />
                      <img
                        class="action-inactive"
                        src="@/assets/images/icons/actions/validation-new-inactive.svg"
                        alt="icon"
                      />
                    </el-button>
                  </li>
                  <li
                    v-if="
                      hasPermission('orders', 'row_actions') &&
                      hasPermission('orders', 'archive_orders') &&
                      scope.row.status != 'ARCHIVED' && !this.validationStatus
                    "
                  >
                    <el-button
                      title="Archive Order"
                      class="delete-button"
                      @click="onDelete(scope.row._id)"
                    >
                      <img
                        class="action-active"
                        src="@/assets/images/icons/actions/archive-icon.svg"
                        alt="icon"
                      />
                      <img
                        class="action-inactive"
                        src="@/assets/images/icons/actions/archive-inactive.svg"
                        alt="icon"
                      />
                    </el-button>
                  </li>
                  <!-- <li v-if="scope.row.count && scope.row.count > 0">
                    <el-button
                      @click="goToNotificationOrderView(scope.row._id)"
                      class="notification_bell"
                    >
                      <img
                        src="@/assets/images/icons/bell-icon.svg"
                        alt="icon"
                      />

                      <span class="icon_tag">
                        {{ scope.row.count }}
                      </span>
                    </el-button>
                  </li> -->
                  <li v-if="hasPermission('orders', 'row_actions') && scope.row.status == 'ARCHIVED' && !this.validationStatus">
                    <el-button
                      class="view-button"
                      @click="onRestore(scope.row)"
                      title="Restore Order"
                    >
                      <img
                        class="action-active"
                        src="@/assets/images/icons/actions/restore-icon-danger.svg"
                        alt="icon"
                      />
                      <img
                        class="action-inactive"
                        src="@/assets/images/icons/actions/restore-icon-danger.svg"
                        alt="icon"
                      />
                    </el-button>
                  </li>
                  <li
                    v-if="hasPermission('orders', 'row_actions') && scope.row.sample_collection_type == 'HOME'"
                    title="Home Collection"
                  >
                    <img
                      class="action-active"
                      style="height: 18px; width: 18px"
                      src="@/assets/images/icons/actions/orders/home_icon.svg"
                      alt="home-icon"
                    />
                  </li>
                  <li v-if="scope.row.sent_to_mlx">
                    <el-tooltip content="Sent to MLX" placement="top">
                      <img
                        style="height: 18px; width: 18px"
                        src="@/assets/images/icons/actions/mlx-icon.png"
                        alt="sent-to-mlx"
                      />
                    </el-tooltip>
                  </li>
                </ul>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="listings listings__pagination">
          <el-pagination
            v-model:currentPage="page"
            v-model:page-size="pageSize"
            :page-sizes="[25, 50, 75, 100]"
            layout="prev, pager, next, jumper, sizes, total"
            :total="getAllOrders.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
    <div class="default-modal">
      <div class="archive-popup">
        <el-dialog top="25vh" width="30%" v-model="archivePopup">
          <div class="dialog-header">
            <span class="header-part"> Reason for Archive </span>
            <el-button
              size="medium"
              class="el-white"
              type="info"
              plain
              @click="archivePopup = false"
            >
              <img src="@/assets/images/close-icon.svg" alt="icon"
            /></el-button>
          </div>

          <el-row :gutter="20" class="popup-text-area">
            <el-col :span="24">
              <el-input
                v-model="textarea"
                type="textarea"
                placeholder="Reason for Archive"
              />
            </el-col>
            <el-col :span="24">
              <div class="dialog-footer">
                <el-button class="blue-cancel-button" plain> CANCEL</el-button>

                <el-button class="blue-save-button"> SUBMIT</el-button>
              </div>
            </el-col>
          </el-row>
        </el-dialog>
      </div>
    </div>
    <ExportOrders
      v-if="exportReportsDialogVisisble"
      v-on:close="exportReportsDialogVisisble = false"
      :selected_search_hospital="(selectedHospital || []).map((h) => h && h._id).filter(Boolean)"
      :selected_search_hospital_names="(selectedHospital || []).map((h) => h && h.name).filter(Boolean)"
      :selected_marketer="hospital_marketer"
      :selected_search_string="search_string"
      :selected_search_ordering_physician="physician"
      :selected_case_type="caseType"
      :selected_dob="date_of_birth"
      :search_case_status="showPartialOnly === true ? ['PARTIAL'] : selectedStatuses"
      :search_first_name="patient_name"
      :pregnancy="pregnant"
      :featureOrders="featureDateOrders"
      :priority="priority"
      :fromDate="fromDate"
      :toDate="toDate"
      :selected_date_type="fromDate || toDate ? dateType : ''"
      :selectedPlatform="testgo_order_type"
      :collectedFromDate="collectedFromDate"
      :collectedToDate="collectedToDate"
      :collectedOrder="collectedOrder"
      :acknowledgementFilter="acknowledgementFilter"
      :technician_name="technician_name"
      :issues="issues"
      :sentToMlxFilter="sentToMlxFilter"
    >
    </ExportOrders>
    <ResonForChange
      v-if="takeReasonDialog"
      v-on:close="takeReasonDialog = false"
      v-on:reasonSubmit="deleteCase"
      title="Reason for Archive"
    ></ResonForChange>
    <ResonForValidation
      v-if="takeValidationReasonDialog"
      v-on:close="takeValidationReasonDialog = false"
      v-on:reasonSubmit="moveToValidation"
      title="Reason for Validation"
    ></ResonForValidation>
    <div class="default-modal">
      <el-dialog
        v-if="takePriorityReasonDialog"
        v-model="takePriorityReasonDialog"
        top="25vh"
        width="36.5%"
        :close-on-click-modal="false"
        @close="closePriorityDialog"
      >
        <div class="dialog-header">
          <span class="header-part">
            Reason for Priority
          </span>
          <el-button
            size="medium"
            class="el-white"
            type="info"
            plain
            @click="closePriorityDialog"
          >
            <img src="@/assets/images/icons/cases/close-btn.svg" alt="icon" />
          </el-button>
        </div>
        <div class="popup-date pb-0">
          <el-input
            type="textarea"
            v-model="priorityReasonText"
            rows="3"
            placeholder="Enter reason"
          />
          <p><b>Note :</b> Minimum 3 characters is required</p>
          <div class="dialog-footer pt-0">
            <el-button
              class="blue-save-button"
              :disabled="!priorityReasonText || priorityReasonText.trim().length < 3"
              @click="markOrderPriority(priorityReasonText)"
            >
              SUBMIT
            </el-button>
          </div>
        </div>
      </el-dialog>
    </div>
    <ReasonForStatusChange
      v-if="takeStatusChangeReasonDialog"
      v-on:close="takeStatusChangeReasonDialog = false"
      v-on:reasonSubmit="unsetOrderCompleteStatus"
      :width="40"
      :title="takeStatusChangeReasonDialogTitle"
    >
    </ReasonForStatusChange>
    <div class="default-modal">
      <el-dialog
        v-model="bulkAccessionPopup"
        top="3vh"
        width="94%"
        custom-class="bulk-accession-dialog"
        :show-close="false"
        :close-on-click-modal="false"
        @close="closeBulkAccessionPopup"
      >
        <!-- Header (the global .default-modal style hides el-dialog's own header) -->
        <div class="ba-header">
          <div class="ba-header-main">
            <div class="ba-title">Bulk Accession</div>
            <div class="ba-subtitle">
              <span class="ba-stat"><strong>{{ bulkSelectedCount }}</strong> of {{ selectedRows.length }} order{{ selectedRows.length !== 1 ? 's' : '' }}</span>
              <span class="ba-stat-sep"></span>
              <span class="ba-stat"><strong>{{ selectedGlobalCaseTypeCount }}</strong> of {{ globalCaseTypeRows.length }} case type{{ globalCaseTypeRows.length !== 1 ? 's' : '' }}</span>
              <span v-if="tubeInfoFetching" class="ba-stat ba-stat-loading">
                <span class="ba-loading-dot"></span><span class="ba-loading-dot"></span><span class="ba-loading-dot"></span>
                Loading tube info
              </span>
            </div>
          </div>
          <button type="button" class="ba-close" aria-label="Close" @click="closeBulkAccessionPopup">✕</button>
        </div>

        <div class="ba-body">
          <!-- Global case type selectors -->
          <div class="ba-global-ct-section">
            <div class="ba-section-head">
              <span class="ba-section-label">Case types to accession</span>
              <span v-if="globalCaseTypeRows.length" class="ba-section-actions">
                <button type="button" class="ba-link" @click="selectAllGlobalCaseTypes(true)">Select all</button>
                <span class="ba-link-sep">·</span>
                <button type="button" class="ba-link" @click="selectAllGlobalCaseTypes(false)">Clear</button>
              </span>
            </div>
            <div class="ba-global-ct-row">
              <div v-if="tubeInfoFetching" class="ba-global-ct-loading">
                <span class="ba-loading-dot"></span><span class="ba-loading-dot"></span><span class="ba-loading-dot"></span>
              </div>
              <template v-else>
                <div
                  v-for="ctRow in globalCaseTypeRows"
                  :key="ctRow.key"
                  class="ba-global-ct-item"
                  :class="{ 'is-checked': !!globalSelectedCaseTypeKeys[ctRow.key], 'is-all-done': ctRow.allDone }"
                >
                  <el-checkbox
                    :model-value="!!globalSelectedCaseTypeKeys[ctRow.key]"
                    :disabled="ctRow.allDone"
                    @change="(val) => toggleGlobalCaseType(ctRow.key, val)"
                  >
                    <span class="ba-ct-name">{{ caseTypeMap[ctRow.case_type] || ctRow.case_type }}</span>
                    <span class="ba-ct-count">{{ ctRow.orderCount }}</span>
                    <span v-if="ctRow.is_sendout" class="ba-flag ba-flag-sendout">Sendout</span>
                    <span v-if="ctRow.is_allergy" class="ba-flag ba-flag-allergy">Allergy</span>
                    <span v-if="ctRow.allDone" class="ba-flag ba-flag-done">All done</span>
                  </el-checkbox>
                </div>
                <span v-if="!globalCaseTypeRows.length" class="ba-empty">No case types found for the selected orders</span>
              </template>
            </div>
          </div>

          <!-- Legend -->
          <div class="ba-legend">
            <span class="ba-legend-item"><span class="ba-test-chip ba-legend-chip"><span class="ba-test-chip-icon">●</span>Test</span> included</span>
            <span class="ba-legend-item"><span class="ba-test-chip ba-legend-chip is-deselected"><span class="ba-test-chip-icon">○</span>Test</span> excluded (click a test to toggle)</span>
            <span class="ba-legend-item"><span class="ba-test-chip ba-legend-chip is-restricted"><span class="ba-test-chip-icon">○</span>Test</span> in-house at another lab</span>
            <span class="ba-legend-item"><span class="ba-ct-tag-sendout">S</span> sendout</span>
            <span class="ba-legend-item"><span class="ba-ct-tag-allergy">A</span> allergy</span>
          </div>

          <!-- Orders table -->
          <el-table
            :data="selectedRows"
            class="ba-table"
            style="width: 100%"
            :max-height="bulkTableMaxHeight"
            :row-class-name="({ row }) => (bulkSelectedOrderIds[row._id] ? '' : 'ba-row-unselected')"
          >
            <el-table-column width="48" align="center">
              <template #header>
                <el-checkbox
                  :model-value="allBulkOrdersSelected"
                  :indeterminate="someBulkOrdersSelected && !allBulkOrdersSelected"
                  @change="toggleAllBulkOrders"
                />
              </template>
              <template #default="scope">
                <el-checkbox
                  :model-value="!!bulkSelectedOrderIds[scope.row._id]"
                  @change="(val) => toggleBulkOrderSelection(scope.row._id, val)"
                />
              </template>
            </el-table-column>
            <el-table-column label="ORDER" min-width="190">
              <template #default="scope">
                <div class="ba-order-id">{{ scope.row.order_id }}</div>
                <div class="ba-patient-name">
                  {{ scope.row.patient_info ? ((scope.row.patient_info.first_name || '') + ' ' + (scope.row.patient_info.last_name || '')).trim() || '--' : '--' }}
                </div>
                <div
                  class="ba-insurance"
                  :class="`is-${bulkOrderBilling(scope.row).kind}`"
                  :title="bulkOrderBilling(scope.row).title"
                >
                  <span class="ba-insurance-label">{{ bulkOrderBilling(scope.row).label }}</span>
                  <span class="ba-insurance-name">{{ bulkOrderBilling(scope.row).name }}</span>
                </div>
                <div v-if="getInsuranceSwapAction(scope.row)" class="ba-ins-secondary">
                  <div class="ba-insurance ba-insurance-secondary" :title="`Secondary: ${getOrderInsurance(scope.row).secondaryPayor}`">
                    <span class="ba-insurance-label">2ND</span>
                    <span class="ba-insurance-name">{{ getOrderInsurance(scope.row).secondaryPayor }}</span>
                  </div>
                  <button
                    type="button"
                    class="ba-ins-swap"
                    :class="{ 'is-undo': getInsuranceSwapAction(scope.row) === 'undo', 'is-busy': bulkInsuranceSwapping[scope.row._id] }"
                    :disabled="!!bulkInsuranceSwapping[scope.row._id]"
                    :title="getInsuranceSwapAction(scope.row) === 'swap'
                      ? `Swap: make ${getOrderInsurance(scope.row).secondaryPayor} primary`
                      : `Swap back: make ${getOrderInsurance(scope.row).secondaryPayor} primary again`"
                    :aria-label="getInsuranceSwapAction(scope.row) === 'swap' ? 'Swap insurance' : 'Swap insurance back'"
                    @click="onBulkSwapInsurance(scope.row)"
                  >{{ getInsuranceSwapAction(scope.row) === 'swap' ? '⇅' : '↺' }}</button>
                </div>
                <div v-if="scope.row.insurance_swapped_manually" class="ba-ins-swapped-note">Swapped manually</div>
              </template>
            </el-table-column>
            <el-table-column label="CASE TYPES" min-width="170">
              <template #default="scope">
                <div v-if="orderCaseTypeRowsLoading[scope.row._id]" class="ba-loading-cell">
                  <span class="ba-loading-dot"></span>
                  <span class="ba-loading-dot"></span>
                  <span class="ba-loading-dot"></span>
                </div>
                <div v-else class="ba-order-ct-tags">
                  <template v-for="ctRow in (orderCaseTypeRowsMap[scope.row._id] || [])" :key="ctRow.key">
                    <div class="ba-ct-tag-wrap">
                      <span
                        class="ba-ct-tag"
                        :class="{
                          'is-selected': !!globalSelectedCaseTypeKeys[ctRow.key],
                          'is-done': isBulkCaseTypeDone(scope.row, ctRow),
                          'is-skipped': !globalSelectedCaseTypeKeys[ctRow.key],
                          'is-error': orderRowErrors[scope.row._id] && orderRowErrors[scope.row._id][ctRow.key]
                        }"
                      >
                        {{ caseTypeMap[ctRow.case_type] || ctRow.case_type }}
                        <span v-if="ctRow.is_sendout" class="ba-ct-tag-sendout" title="Sendout">S</span>
                        <span v-if="ctRow.is_allergy" class="ba-ct-tag-allergy" title="Allergy">A</span>
                        <span v-if="isBulkCaseTypeDone(scope.row, ctRow)" class="ba-ct-tag-done" title="Already accessioned">✓</span>
                        <span v-else-if="orderRowErrors[scope.row._id] && orderRowErrors[scope.row._id][ctRow.key]" class="ba-ct-tag-err">✕</span>
                      </span>
                      <div
                        v-if="orderRowErrors[scope.row._id] && orderRowErrors[scope.row._id][ctRow.key]"
                        class="ba-ct-error-msg"
                        :class="{ 'ba-ct-error-clickable': orderRowErrors[scope.row._id][ctRow.key].error_code === 'NON_GENTIC_TEST_VALIDATE' }"
                        @click="orderRowErrors[scope.row._id][ctRow.key].error_code === 'NON_GENTIC_TEST_VALIDATE' && openNonGeneticDialog(orderRowErrors[scope.row._id][ctRow.key])"
                      >
                        {{ orderRowErrors[scope.row._id][ctRow.key].message || orderRowErrors[scope.row._id][ctRow.key] }}
                        <span v-if="orderRowErrors[scope.row._id][ctRow.key].error_code === 'NON_GENTIC_TEST_VALIDATE'" class="ba-ct-error-link-hint"> (View Details)</span>
                      </div>
                    </div>
                  </template>
                  <span v-if="!(orderCaseTypeRowsMap[scope.row._id] || []).length" class="ba-empty">—</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="TESTS" min-width="240">
              <template #default="scope">
                <div v-if="orderCaseTypeRowsLoading[scope.row._id]" class="ba-loading-cell">
                  <span class="ba-loading-dot"></span>
                  <span class="ba-loading-dot"></span>
                  <span class="ba-loading-dot"></span>
                </div>
                <div v-else class="ba-tests-list">
                  <template v-for="tube in (orderTubeInfoMap[scope.row._id] || [])" :key="tube._id">
                    <template v-for="test in (tube.tests || [])" :key="test.lab_order_code">
                      <span
                        v-if="isTestVisible(test)"
                        class="ba-test-chip"
                        :class="{
                          'is-deselected': isTestDeselected(scope.row._id, test.lab_order_code),
                          'is-restricted': isTestRestricted(test)
                        }"
                        :title="isTestRestricted(test) ? `${test.title} — In-house at another lab` : `${test.title} — click to ${isTestDeselected(scope.row._id, test.lab_order_code) ? 'include' : 'exclude'}`"
                        @click="!isTestRestricted(test) && toggleOrderTest(scope.row._id, test.lab_order_code)"
                      >
                        <span class="ba-test-chip-icon">{{ isTestRestricted(test) || isTestDeselected(scope.row._id, test.lab_order_code) ? '○' : '●' }}</span>
                        <span class="ba-test-chip-text">{{ test.title }}</span>
                      </span>
                    </template>
                  </template>
                  <span v-if="!hasVisibleTests(scope.row._id)" class="ba-empty">—</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="TUBES / SAMPLE ACKNOWLEDGMENT" min-width="420">
              <template #default="scope">
                <div v-if="orderCaseTypeRowsLoading[scope.row._id]" class="ba-loading-cell">
                  <span class="ba-loading-dot"></span>
                  <span class="ba-loading-dot"></span>
                  <span class="ba-loading-dot"></span>
                </div>
                <div v-else-if="(orderTubeInfoMap[scope.row._id] || []).length" class="ba-ack-list">
                  <div
                    v-for="tube in orderTubeInfoMap[scope.row._id]"
                    :key="tube._id || tube.tube_name"
                    class="ba-ack-item"
                  >
                    <span class="ba-ack-tube-name" :title="tube.tube_name">{{ tube.tube_name }}</span>
                    <el-select
                      :model-value="(orderAckStatusMap[scope.row._id] || {})[tube.tube_name]"
                      placeholder="Status"
                      size="small"
                      class="ba-ack-select"
                      :class="{
                        'is-received': (orderAckStatusMap[scope.row._id] || {})[tube.tube_name] === 'RECEIVED',
                        'is-not-received': (orderAckStatusMap[scope.row._id] || {})[tube.tube_name] === 'NOT_RECEIVED',
                        'is-changed': (orderAckStatusMap[scope.row._id] || {})[tube.tube_name] !== (orderAckOriginalMap[scope.row._id] || {})[tube.tube_name]
                      }"
                      @update:model-value="(val) => updateOrderAckStatus(scope.row._id, tube.tube_name, val)"
                    >
                      <el-option label="Received" value="RECEIVED" />
                      <el-option label="Not Received" value="NOT_RECEIVED" />
                    </el-select>
                    <el-select
                      :model-value="(orderLabMap[scope.row._id] || {})[tube.tube_name] || ''"
                      placeholder="Lab"
                      size="small"
                      clearable
                      filterable
                      class="ba-ack-lab-select"
                      @update:model-value="(val) => updateOrderLab(scope.row._id, tube.tube_name, val)"
                    >
                      <el-option
                        v-for="lab in getAllLabs"
                        :key="lab._id"
                        :label="lab.name"
                        :value="lab.name"
                      ></el-option>
                    </el-select>
                    <div
                      v-if="hasAckByInfo(tube) && (tube.acknowledged_by || tube.acknowledged_at || tube.lab)"
                      class="ba-ack-by"
                      :title="[tube.acknowledged_by, tube.acknowledged_at, tube.lab].filter(Boolean).join(' · ')"
                    >
                      <span class="ba-ack-by-check">✓</span>
                      {{ [tube.acknowledged_by, tube.acknowledged_at, tube.lab].filter(Boolean).join(' · ') }}
                    </div>
                  </div>
                </div>
                <span v-else class="ba-empty">No tubes</span>
              </template>
            </el-table-column>
            <el-table-column label="VENDOR" min-width="150">
              <template #default="scope">
                <el-select
                  :model-value="orderVendorMap[scope.row._id] || ''"
                  placeholder="Select vendor"
                  clearable
                  size="small"
                  class="ba-vendor-select"
                  @update:model-value="(val) => updateOrderVendor(scope.row._id, val)"
                >
                  <el-option
                    v-for="vendor in nonMlxVendors"
                    :key="vendor.code"
                    :label="vendor.name"
                    :value="vendor.code"
                  ></el-option>
                </el-select>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <template #footer>
          <div class="ba-footer">
            <div class="ba-footer-status">
              <div v-if="bulkResultStatus" class="ba-result-bar" :class="`ba-result-${bulkResultStatus}`">
                <span class="ba-result-icon">
                  {{ bulkResultStatus === 'success' ? '✓' : bulkResultStatus === 'partial' ? '!' : '✕' }}
                </span>
                <span>{{ bulkResultMessage }}</span>
              </div>
              <div v-if="hasUnconfirmedAckChanges && bulkResultStatus !== 'success'" class="ba-result-bar ba-result-partial">
                <span class="ba-result-icon">!</span>
                <span>Sample status changed. Click <strong>Confirm Acknowledgment</strong> before creating accessions.</span>
              </div>
            </div>
            <div class="ba-footer-actions">
              <el-button class="ba-btn ba-btn-ghost" @click="closeBulkAccessionPopup">
                {{ bulkResultStatus ? 'Close' : 'Cancel' }}
              </el-button>
              <el-button
                v-if="bulkCreatedCaseIds.length"
                class="ba-btn ba-btn-outline"
                @click="onBulkPrintBarCodes"
              >
                Print Accession Barcodes
              </el-button>
              <el-button
                v-if="bulkCreatedOrderIds.length"
                class="ba-btn ba-btn-outline"
                @click="onBulkPrintOrderBarCodes"
              >
                Print Order Barcodes
              </el-button>
              <el-button
                v-if="bulkCreatedCCCaseIds.length"
                class="ba-btn ba-btn-outline"
                @click="onBulkPrintTestBarCodes"
              >
                Print Test Barcodes
              </el-button>
              <el-button
                class="ba-btn"
                :class="hasUnconfirmedAckChanges ? 'ba-btn-primary' : 'ba-btn-outline'"
                :loading="bulkAckSaving"
                :disabled="tubeInfoFetching || !someBulkOrdersSelected"
                @click="confirmBulkAcknowledgment"
              >
                Confirm Acknowledgment
              </el-button>
              <el-button
                class="ba-btn ba-btn-primary"
                :loading="bulkAccessionLoading"
                :disabled="tubeInfoFetching || bulkResultStatus === 'success' || !someBulkOrdersSelected || !selectedGlobalCaseTypeCount || hasUnconfirmedAckChanges"
                @click="confirmBulkAccession"
              >
                Create Accessions
              </el-button>
            </div>
          </div>
        </template>
      </el-dialog>
    </div>
    <!-- NON_GENTIC_TEST_VALIDATE popup — same as single-order create accession flow -->
    <ExistingCaseDetails
      v-if="bulkNonGeneticDialog"
      :convert="true"
      :existedCase="bulkNonGeneticExistingDetails"
      :existedOrder="bulkNonGeneticOrderData"
      :caseType="bulkNonGeneticCaseType"
      validationErrorMsg="NON_GENTIC_TEST_VALIDATE"
      v-on:closePopupDialog="bulkNonGeneticDialog = false"
    />
    <OrderRejectDialog
      v-if="orderRejectDialogVisible"
      :visible="orderRejectDialogVisible"
      :orderData="orderRejectRow"
      :tubeInfo="orderRejectTubeInfo"
      :loading="orderRejectLoading"
      :submitting="orderRejectSubmitting"
      :mode="orderRejectMode"
      @close="closeOrderRejectDialog"
      @submit="submitOrderReject"
    />
    <OptionsRejectPopup
      :visible="showRejectOptionsPopup"
      :mode="rejectOptionsMode"
      @close="closeRejectOptionsPopup"
      @partial="onPartialRejectOption"
      @complete="onCompleteRejectOption"
      @activate="onPartialActivateOption"
    />
    <ReasonForStatusMoving
      v-if="takeStatusMovingReasonDialog"
      v-on:close="takeStatusMovingReasonDialog = false"
      v-on:reasonSubmit="submitCompleteRejection"
      :width="40"
      :title="takeStatusChangeReasonDialogTitle"
    >
    </ReasonForStatusMoving>
  </section>
</template>

<script>
import { defineAsyncComponent, toRaw } from "vue";
import { mapGetters } from "vuex";
import moment from "moment";
const ExportOrders = defineAsyncComponent(() =>
  import("@/components/orders/ExportOrders")
);
const ReasonForStatusChange = defineAsyncComponent(() =>
  import("@/components/misc/OrderReasonForChange")
);
import caseConstants from "@/config/constants/caseConstants";
import {
  ordersExtraSearchOptions,
  pregnencyOptions,
} from "@/config/constants/searchConstants";
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper";
import FilterHelper from "@/mixins/FilterHelper";
import caseDataHelper from "@/mixins/caseDataHelper";
const ResonForChange = defineAsyncComponent(() =>
  import("@/components/misc/ResonForChange")
);
const ResonForValidation = defineAsyncComponent(() =>
  import("@/components/misc/ResonForChange")
);
const ExistingCaseDetails = defineAsyncComponent(() =>
  import("@/components/cases/ExisistedCaseInfo")
);
const OrderRejectDialog = defineAsyncComponent(() =>
  import("@/components/orders/OrderRejectDialog")
);
const ReasonForStatusMoving = defineAsyncComponent(() =>
  import("@/components/misc/OrderReasonForChange")
);

import OptionsRejectPopup from "@/components/cases/OptionsRejectPopup.vue";
import {
  successNotification,
  errorNotification,
} from "@/helpers/notifications";
import FilterPreferencesMixin from "@/mixins/FilterPreferencesMixin";
import { ElMessageBox } from "element-plus";
import { getInsuranceSwapAction, getOrderInsurance } from "@/helpers/insuranceSwap";
import SearchShortcutMixin from "@/mixins/SearchShortcutMixin";
import ListRowShortcutsMixin from "@/mixins/ListRowShortcutsMixin";
import draggable from "vuedraggable";
export default {
  name: "",
  components: {
    ExportOrders,
    ResonForChange,
    ResonForValidation,
    ReasonForStatusChange,
    ExistingCaseDetails,
    OrderRejectDialog,
    OptionsRejectPopup,
    ReasonForStatusMoving,
    draggable,
  },
  props: {
    selectedStatuses: {
      default: [],
    },
    issues: {
      type: Boolean,
      default: false,
    },
    collectedOrder: {
      type: Boolean,
      default: false,
    },
    validationStatus:{
      default: false,
    },
    sampleAckTab: {
      type: Boolean,
      default: false,
    },
    insuranceOnHold: {
      type: Boolean,
      default: false,
    },
    samplePending: {
      type: Boolean,
      default: false,
    }
  },
  mixins: [FilterHelper, UserPermissionsHelper, caseDataHelper, FilterPreferencesMixin, SearchShortcutMixin, ListRowShortcutsMixin],
  rowShortcutNoun: "order",
  // The search box already carries this ref.
  searchShortcutRef: "orderInput",
  data() {
    return {
      marketerData: [],
      // Monotonic id for getOrders requests; guards against stale responses
      // overwriting the results/URL of a newer request.
      fetchSeq: 0,
      scrollPosition: null,
      showFilters: false,
      filterGroupKey: "orders",
      filterMeta: [
        { key: "ordering_facility", label: "Ordering Facility" },
        { key: "physician", label: "Physician" },
        { key: "case_type", label: "Case Type" },
        { key: "sales_rep", label: "Sales Rep" },
        { key: "priority", label: "Priority" },
        { key: "platform", label: "Platform" },
        { key: "technician_name", label: "Technician Name" },
        { key: "acknowledgement", label: "Acknowledgement" },
        { key: "partial_only", label: "Partial" },
        { key: "partial_reason", label: "Partial Reason" },
        { key: "sent_to_mlx", label: "Sent to MLX" },
      ],
      marketingManagerData: [],
      userLoading: false,
      disabledDate: (time) => {
        return time.getTime() > Date.now();
      },
      archivePopup: false,
      page: 1,
      pageSize: 25,
      caseType: [],
      caseTypeMap: caseConstants.CASE_TYPE_MAP,
      conversionStatusLabel: { OK: "Ready", REDIRECT: "Redirect", BLOCK: "Blocked" },
      caseTypes: caseConstants.CASE_TYPES_DATA,
      searchOptions: ordersExtraSearchOptions,
      platformCategoriesMap: [],
      testgo_order_type:[],
      pregnencyOptions: pregnencyOptions,
      loading: false,
      search_string: "",
      patient_name: "",
      hospital: "",
      selectedHospital: [],
      hospital_marketer: [],
      date_of_birth: "",
      physician: [],
      searchBy: "",
      orderBy: "",
      orderType: "",
      exportReportsDialogVisisble: false,
      takeReasonDialog: false,
      requestArchiveOrder: "",
      hospitalsData: { data: [] },
      physiciansData: {},
      technicianData: [],
      technician_name: [],
      hospitalsLoading: false,
      physiciansLoading: false,
      technicianLoading: false,
      pregnant: "",
      selectedCaseIds: [],
      selectedRows: [],
      bulkAccessionPopup: false,
      bulkAccessionLoading: false,
      bulkTableMaxHeight: 420,
      bulkInsuranceSwapping: {},
      orderVendorMap: {},
      orderLabMap: {},
      sendoutLabsFetching: false,
      sendoutLabsFetched: false,
      zebraPrinterStatus: null,
      tubeInfoFetching: false,
      selectedCaseTypesMap: {},
      orderTubeInfoMap: {},
      orderAckStatusMap: {},
      orderAckOriginalMap: {},
      bulkAckSaving: false,
      orderCaseTypeRowsMap: {},
      orderCaseTypeRowsLoading: {},
      selectedCaseTypeKeysMap: {},
      globalSelectedCaseTypeKeys: {},
      orderDatesMap: {},
      orderRowErrors: {},
      orderDeselectedTestsMap: {},
      bulkSelectedOrderIds: {},
      bulkNonGeneticDialog: false,
      bulkNonGeneticExistingDetails: [],
      bulkNonGeneticCaseType: [],
      bulkNonGeneticOrderData: {},
      orderRejectDialogVisible: false,
      orderRejectRow: {},
      orderRejectTubeInfo: [],
      orderRejectMode: "reject",
      orderRejectLoading: false,
      bulkResultStatus: null,
      bulkResultMessage: "",
      bulkCreatedCaseIds: [],
      bulkCreatedCCCaseIds: [],
      bulkCreatedOrderIds: [],
      requestValidationOrder: "",
      takeValidationReasonDialog: false,
      takePriorityReasonDialog: false,
      priorityOrder: null,
      priorityReasonText: "",
      reOrderWithTestInfo: "",
      reOrderWithInsurance: "",
      reOrderWithAddress: "",
      reOrderId: "",
      barcodeNumber: "",
      showRejectOptionsPopup: false,
      rejectOptionsContext: {
        row: null,
        index: null,
      },
      rejectOptionsMode: "reject",
      takeStatusMovingReasonDialog: false,
      rejectingOrderForComplete: null,
      rejectActionType: "reject",
      takeStatusChangeReasonDialog: false,
      takeStatusChangeReasonDialogTitle: "",
      orderIds: [],
      counts: [],
      featureDateOrders: "",
      priority: "",
      dateType: "created_at",
      dateTypeOptions: [
        { label: "Created On", value: "created_at" },
        { label: "Collection Date", value: "collection_date" },
        { label: "Received Date", value: "received_date" },
        { label: "Order Placed On", value: "order_placed_at" },
      ],
      fromDate: "",
      toDate: "",
      collectedFromDate: "",
      collectedToDate: "",
      acknowledgementFilter: "",
      showPartialOnly: null,
      partialReasonFilter: [],
      partialReasonOptions: [
        { label: "Pending at MLX", value: "Pending at MLX" },
        { label: "Pending at Lab", value: "Pending at Lab" },
      ],
      sentToMlxFilter: "",
      orderPriorityTypes: caseConstants.CASE_PROIROTY_TPYES,
      searchDebounceTimer: null,
      scanDebounceTimer: null,
      unsetCompleteStatusChangeCase: null,
      toStatus: null,
      caseIndex: null,
      // Caches for formatting functions to improve table rendering performance
      statusTextCache: new Map(),
      caseTypesCache: new WeakMap(),
      patientNameCache: new WeakMap(),
    };
  },
  created() {
    this.searchOptions = this.searchOptions.filter((option) => option.active);
  },
  computed: {
    // Partial reason is derived on the client, so its filter applies to the loaded page.
    displayedOrders() {
      const orders = this.getAllOrders?.data || [];
      if (!this.partialReasonFilter || !this.partialReasonFilter.length) {
        return orders;
      }
      return orders.filter((order) => this.partialReasonFilter.includes(this.getPartialReason(order)));
    },
    // Reflect the chosen date type in the From/To placeholders so it's clear
    // which date the range filters on (the type is set in Customize Filters).
    dateTypeLabel() {
      const opt = this.dateTypeOptions.find((o) => o.value === this.dateType);
      return opt ? opt.label : "Created On";
    },
    fromDatePlaceholder() {
      return `From (${this.dateTypeLabel})`;
    },
    toDatePlaceholder() {
      return `To (${this.dateTypeLabel})`;
    },
    getStatusType() {
      return (statusVal) => {
        const status = statusVal;
        const statusMap = {
          ACTIVE: "default",
          COMPLETED: "success",
          TEST_REPORTS: "warning",
          PARTIAL: "info",
          ARCHIVED: "danger",
          REJECTED: "darkDanger",
          PARTIALLY_REJECTED: "darkDanger",
          PENDING: "danger",
        };
        return statusMap[status] || "";
      };
    },
    // Conversion guidance is only for orders waiting to be accessioned (ACTIVE tab).
    // Sample Ack / On Hold also pass ACTIVE statuses, so match the route, not the statuses.
    showConversionColumn() {
      return this.$route?.name === "ActiveOrders";
    },
    // Memoized function to format status text
    formatStatusText() {
      return (status) => {
        if (!status) return "";
        // Use instance cache for status text (status strings are primitive, can use Map)
        if (this.statusTextCache.has(status)) {
          return this.statusTextCache.get(status);
        }
        let formatted;
        if (status === "COMPLETED") {
          formatted = "COMPLETED";
        } else {
          formatted = status.split("_").join(" ");
        }
        this.statusTextCache.set(status, formatted);
        return formatted;
      };
    },
    ...mapGetters("orders", [
      "getAllOrders",
      "getCaseDeleteStatus",
      "getCaseDeleteError",
      "getUpdateValidationStatus",
      "getScanOrderDetails",
      "getScanOrderMeta",
      "getUpdateOrderStatus",
      "getRestoreOrderStatus",
      "getRestoreOrderError",
      "getTestTubeInfoById",
      "getAccessionCreateStatus",
      "getAccessionCreationValidationErrorCode",
      "getCreateAccessionDetails",
    ]),
    ...mapGetters("hospitals", ["getAllHospitals"]),
    ...mapGetters("physicians", ["getAllPhysicians"]),
    ...mapGetters("auth", ["getAuthenticatedUser", "getUserPrimaryLabId", "getUserAllowedLabs"]),
    ...mapGetters("userAccounts", ["getAllUsers"]),
    ...mapGetters("vendors", ["getVendorCodes", "getAllVendors"]),
    ...mapGetters("labs", ["getAllLabs"]),
    nonMlxVendors() {
      return (this.getAllVendors || []).filter((v) => !v.is_mlx);
    },
    getCaseTypes() {
      return (caseTypes) => {
        return (caseTypes || []).map((e) => this.caseTypeMap[e]);
      };
    },
    globalCaseTypeRows() {
      const seenKeys = new Map();
      for (const row of this.selectedRows) {
        const ctRows = this.orderCaseTypeRowsMap[row._id] || [];
        for (const ctRow of ctRows) {
          if (this.isOrderCaseTypeRejected(row, ctRow.case_type)) continue;
          if (!seenKeys.has(ctRow.key)) {
            seenKeys.set(ctRow.key, { ...ctRow, orderCount: 0, allDone: true });
          }
          const entry = seenKeys.get(ctRow.key);
          entry.orderCount++;
          const isDone = (row.accession_data || []).some((a) => a.case_type === ctRow.case_type && !!(a.sendout_required) === !!ctRow.is_sendout && !!(a.is_allergy) === !!ctRow.is_allergy);
          if (!isDone) entry.allDone = false;
        }
      }
      return Array.from(seenKeys.values());
    },
    allBulkOrdersSelected() {
      return this.selectedRows.length > 0 && this.selectedRows.every((r) => !!this.bulkSelectedOrderIds[r._id]);
    },
    someBulkOrdersSelected() {
      return this.selectedRows.some((r) => !!this.bulkSelectedOrderIds[r._id]);
    },
    bulkSelectedCount() {
      return this.selectedRows.filter((r) => !!this.bulkSelectedOrderIds[r._id]).length;
    },
    selectedGlobalCaseTypeCount() {
      return this.globalCaseTypeRows.filter((ct) => !!this.globalSelectedCaseTypeKeys[ct.key]).length;
    },
    // True when a Received/Not Received status was changed in the dialog but
    // not yet saved via "Confirm Acknowledgment" — Create Accessions must be
    // blocked until the change is confirmed so the audit trail reflects it.
    hasUnconfirmedAckChanges() {
      const rowsToProcess = this.selectedRows.filter((r) => !!this.bulkSelectedOrderIds[r._id]);
      for (const row of rowsToProcess) {
        const current = this.orderAckStatusMap[row._id] || {};
        const original = this.orderAckOriginalMap[row._id] || {};
        for (const tubeName in current) {
          if (current[tubeName] !== original[tubeName]) return true;
        }
      }
      return false;
    },
    // Memoized version to avoid repeated calls for same row
    getFormattedCaseTypes() {
      return (row) => {
        if (!row?.case_types) return [];
        // Use WeakMap cache (automatically handles object references)
        if (this.caseTypesCache.has(row)) {
          return this.caseTypesCache.get(row);
        }
        const formatted = this.getCaseTypes(row.case_types);
        this.caseTypesCache.set(row, formatted);
        return formatted;
      };
    },
    // Memoized patient name formatting
    getFormattedPatientName() {
      return (row) => {
        if (!row?.patient_info) return "";
        // Use WeakMap cache for object-based caching
        if (this.patientNameCache.has(row)) {
          return this.patientNameCache.get(row);
        }
        const formatted = this.convertToUppercase(
          this.getPatientNameByInfo(row.patient_info)
        );
        this.patientNameCache.set(row, formatted);
        return formatted;
      };
    },
    currentLabLastWord() {
      const labs = this.getUserAllowedLabs;
      const primaryLabId = this.getUserPrimaryLabId;
      if (!labs || !primaryLabId) return "";
      const lab = labs.find((l) => l._id === primaryLabId);
      if (!lab || !lab.name) return "";
      return lab.name.trim().split(/\s+/).pop().toUpperCase();
    },
    hasPayorMapMatch() {
      return (row) => {
        const map = row.commercial_insurance_location_payor_map;
        if (!map || typeof map !== "object" || !Object.keys(map).length) return false;
        if (!this.currentLabLastWord) return false;
        return Object.keys(map).some((k) => k.toUpperCase() === this.currentLabLastWord);
      };
    },
    // Sample-acknowledgement entries for the orders currently checked via
    // the row checkboxes (same selection used for Create Accessions) —
    // that's the gate for what gets sent, not whether the status changed.
    bulkAckPayload() {
      const payload = [];
      const rowsToProcess = this.selectedRows.filter((r) => !!this.bulkSelectedOrderIds[r._id]);
      for (const row of rowsToProcess) {
        const orderId = row._id;
        const tubes = this.orderTubeInfoMap[orderId] || [];
        const statuses = this.orderAckStatusMap[orderId] || {};
        const labs = this.orderLabMap[orderId] || {};
        for (const tube of tubes) {
          const tubeName = tube.tube_name;
          const status = statuses[tubeName];
          if (!status) continue;
          payload.push({
            order_id: orderId,
            tube_name: tubeName,
            status,
            order_codes: this.getSelectedOrderCodesForTube(orderId, tubeName),
            sendout_lab: labs[tubeName] || "",
          });
        }
      }
      return payload;
    },
  },
  async mounted() {
    // Auto-focus the QR field once on load so a scan works immediately. We do
    // NOT re-assert focus afterwards: the moment the user moves to another
    // field (e.g. Order Id / Patient), focus stays there until they manually
    // click back into the QR field.
    if (this.hasPermission("cases", "create_case")) {
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        }
      });
    }
    this.setQueryParamsData();
    await this.fetchAllOrders();
    await this.fetchVendors();
    this.loadFilterPreferences();
  },
  watch: {
    getVendorCodes: {
      handler(codes) {
        if (codes && codes.length) {
          this.platformCategoriesMap = codes;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  beforeUnmount() {
    // Clear any pending debounce timers
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.scanDebounceTimer) {
      clearTimeout(this.scanDebounceTimer);
    }
    localStorage.removeItem("searchQueryFacility");
    localStorage.removeItem("searchQueryPhysician");
  },
  methods: {
    getPartialReason(order) {
      if (!order || order.status !== "PARTIAL") {
        return null;
      }
      const orderCodes = Array.isArray(order.order_codes) ? order.order_codes : [];
      if (!orderCodes.length) {
        return null;
      }
      const isSentToMLX = order.sent_to_mlx === true || order.order_type === "MLX";
      if (!isSentToMLX) {
        return "Pending at Lab";
      }
      const collectedTests = Array.isArray(order.collected_tests) ? order.collected_tests : [];
      return orderCodes.length !== collectedTests.length ? "Pending at MLX" : "Pending at Lab";
    },
    isFilterAllowedForKey(key) {
      if (key === "partial_only") {
        return Array.isArray(this.selectedStatuses) && this.selectedStatuses.includes("PARTIAL");
      }
      if (key === "partial_reason") {
        return this.showPartialOnly === true;
      }
      return true;
    },
    async remoteMethodFacility(query) {
      if (query !== "") {
        localStorage.setItem("searchQueryFacility", query);
        await this.fetchAllHospitals(query);
      } else {
        await this.fetchAllHospitals();
      }
    },
    async fetchVendors() {
      await this.$store.dispatch("vendors/fetchVendors", { skip: 0, limit: 100 });
      // Directly update platformCategoriesMap after fetch
      const codes = this.getVendorCodes;
      console.log("After fetchVendors - codes:", codes);
      if (codes && codes.length) {
        this.platformCategoriesMap = codes;
      }
    },
    onHospitalFocus() {
      if (!this.hospitalsData.data || !this.hospitalsData.data.length) {
        this.fetchAllHospitals();
      }
    },
    onHospitalChange(value) {
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      this.hospital = selected.length ? selected[0]._id : "";
      this.physician = [];
      this.physiciansData = { data: [] };
      this.handleCurrentChange(1);
    },
    async onRestore(order) {
      this.$confirm("Are you sure to Restore order?", "Warning", {
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        type: "warning",
      }).then(() => {
        this.restoreOrder(order);
      });
    },
    async restoreOrder(order) {
      this.loading = true;
      let params = {
        order_id: order._id,
        status: order.accession_id ? "COMPLETED" : "ACTIVE",
      };
      await this.$store.dispatch("orders/restoreArchivedOrder", params);
      if (this.getRestoreOrderStatus) {
        successNotification("Order Restored Successfully");
        await this.fetchAllOrders(this.page);
      } else {
        errorNotification(
          this.getRestoreOrderError || "An Error occured while restoring."
        );
      }
      this.loading = false;
    },
    getOrderDetails() {
      // A QR/barcode scanner emits the whole value in a rapid burst of input
      // events; debounce so we act once on the final value instead of on each
      // partial (which could otherwise open an order prematurely).
      if (this.scanDebounceTimer) {
        clearTimeout(this.scanDebounceTimer);
      }
      this.scanDebounceTimer = setTimeout(() => {
        this.handleScannedOrder();
      }, 200);
    },
    async handleScannedOrder() {
      try {
        let updateBarcode = this.barcodeNumber.trim();
        if (!updateBarcode) {
          return;
        }
        const scanValue = updateBarcode.toLocaleUpperCase();

        // Single API call: resolves the scanned code and returns how many
        // orders are in its family (a TestGO order and its linked
        // sendout/medical-group order share actual_testgo_order_id). The count
        // is status-agnostic and lab-scoped on the server.
        await this.$store.dispatch("orders/fetchScanOrderById", {
          orderId: scanValue,
        });
        const scanned = this.getScanOrderDetails;
        if (!scanned) {
          // Nothing matched the scan.
          return;
        }

        const meta = this.getScanOrderMeta || {};
        const linkId = meta.link_id || scanned.actual_testgo_order_id || scanValue;
        const count = meta.count || 1;

        this.barcodeNumber = "";

        if (count > 1) {
          // Multiple linked orders — behave like a search: show the ALL list
          // filtered by the linking id so every sibling is visible, instead of
          // opening just one of them.
          if (this.$route.name === "AllOrders") {
            this.search_string = linkId;
            this.page = 1;
            this.fetchAllOrders();
          } else {
            this.$router.push({
              name: "AllOrders",
              query: { selectedOrderString: linkId },
            });
          }
          return;
        }

        // Single order — open it directly (scan-to-open).
        this.goToOrdersView(scanned._id);
      } catch (err) {
        console.log(err);
      }
    },
    async remoteMethodPhysician(query) {
      if (query !== "") {
        localStorage.setItem("searchQueryPhysician", query);
        await this.fetchAllPhysicians(query);
      } else {
        await this.fetchAllPhysicians();
      }
    },
    onPhysicianFocus() {
      if (!this.physiciansData.data || !this.physiciansData.data.length) {
        this.fetchAllPhysicians();
      }
    },
    async moveToValidations(validation) {
      this.requestValidationOrder = validation;
      this.takeValidationReasonDialog = true;
    },
    async moveToValidation(reason) {
      try {
        this.loading = true;
        let params = {
          id: this.requestValidationOrder,
          status: true,
          validation_reason: reason,
        };
        await this.$store.dispatch("orders/changeCaseValidationStatus", params);
        if (this.getUpdateValidationStatus) {
          successNotification(
            "Labsquire order moved to validation Labsquire Orders"
          );
          await this.fetchAllOrders();
        } else {
          errorNotification(
            "Error while moving labsquire order to validation labsquire orders"
          );
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    openPriorityDialog(row) {
      // Already flagged as priority — nothing to do, don't reopen the popup.
      if (row.is_priority) {
        return;
      }
      this.priorityOrder = row;
      this.priorityReasonText = "";
      this.takePriorityReasonDialog = true;
    },
    closePriorityDialog() {
      this.takePriorityReasonDialog = false;
      this.priorityOrder = null;
      this.priorityReasonText = "";
    },
    async markOrderPriority(reason) {
      const order = this.priorityOrder;
      this.takePriorityReasonDialog = false;
      if (!order) return;
      try {
        this.loading = true;
        const user = this.getAuthenticatedUser || {};
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        const priorityReason = fullName
          ? `${reason} - Added by ${fullName}`
          : reason;
        const result = await this.$store.dispatch("orders/markOrderPriority", {
          order_id: order._id,
          is_priority: true,
          priority_reason: priorityReason,
        });
        if (result && result.success !== false) {
          successNotification("Order marked as priority successfully");
          await this.fetchAllOrders();
        } else {
          errorNotification(
            (result && result.message) || "Error while marking order as priority"
          );
        }
      } catch (error) {
        console.log(error);
        errorNotification(
          error.response?.data?.message ||
            error.message ||
            "Error while marking order as priority"
        );
      } finally {
        this.loading = false;
        this.priorityOrder = null;
        this.priorityReasonText = "";
      }
    },
    async openOrderRejectDialog(row, mode = "reject") {
      this.orderRejectRow = row;
      this.orderRejectTubeInfo = [];
      this.orderRejectMode = mode;
      this.orderRejectDialogVisible = true;
      this.orderRejectLoading = true;
      try {
        const tubeInfoMap = await this.$store.dispatch(
          "orders/fetchOrderTestsTubeInfoBulk",
          { ids: [row._id], includeRejected: mode === "activate" }
        );
        this.orderRejectTubeInfo = JSON.parse(JSON.stringify(tubeInfoMap[row._id] || []));
      } catch (error) {
        console.log(error);
        errorNotification("Unable to load order tube information");
      } finally {
        this.orderRejectLoading = false;
      }
    },
    closeOrderRejectDialog() {
      this.orderRejectDialogVisible = false;
      this.orderRejectRow = {};
      this.orderRejectTubeInfo = [];
      this.orderRejectMode = "reject";
    },
    async submitOrderReject(payload) {
      try {
        this.orderRejectSubmitting = true;
        const isActivation = this.orderRejectMode === "activate";
        const result = isActivation
          ? await this.$store.dispatch("orders/partialActivateOrder", payload)
          : await this.$store.dispatch("orders/rejectOrder", payload);
        const updateStatus = result || this.getUpdateOrderStatus;
        if (updateStatus && updateStatus.success !== false) {
          successNotification(updateStatus.message || (isActivation ? "Order activated successfully" : "Order rejection submitted successfully"));
          this.closeOrderRejectDialog();
          await this.fetchAllOrders();
        } else {
          errorNotification(updateStatus?.message || (isActivation ? "Error while activating order" : "Error while rejecting order"));
          this.closeOrderRejectDialog();
        }
      } catch (error) {
        console.log(error);
        errorNotification(error.response?.data?.message || error.message || (this.orderRejectMode === "activate" ? "Error while activating order" : "Error while rejecting order"));
        this.closeOrderRejectDialog();
      } finally {
        this.orderRejectSubmitting = false;
      }
    },
    // conversion_guidance is computed server-side for every conversion lab; show the
    // entries for the lab this user accessions into.
    getConversionGuidance(row) {
      const entries = row?.conversion_guidance?.entries || [];
      const labId = this.getUserPrimaryLabId ? String(this.getUserPrimaryLabId) : "";
      return entries.filter((entry) => entry.lab === labId);
    },
    // Short, actionable line for the cell; the tooltip carries the full messages.
    conversionHelperText(row, entry) {
      const date = this.isFutureGuidanceDate(entry.earliest_received_date)
        ? ` · Recv ≥ ${moment(entry.earliest_received_date).format("MM-DD")}`
        : "";
      if (entry.status === "BLOCK") {
        const allowed = this.getOtherLabGuidance(row, entry)
          .filter((other) => other.status !== "BLOCK")
          .map((other) => this.shortLabName(other.lab_name));
        return allowed.length ? `Convert at ${allowed.join(" / ")}` : "Blocked";
      }
      if (entry.status === "REDIRECT") {
        return `Registers at ${this.shortLabName(entry.redirect_lab_name)}${date}`;
      }
      return `Ready${date}`;
    },
    // The same case type at the other conversion labs (all labs come back with the list).
    getOtherLabGuidance(row, entry) {
      return (row?.conversion_guidance?.entries || []).filter(
        (other) => other.case_type === entry.case_type && other.lab !== entry.lab
      );
    },
    shortLabName(name) {
      return String(name || "").replace(/^TestGo\s*-?\s*/i, "");
    },
    // A received-date floor only matters to the user while it is still ahead of today.
    isFutureGuidanceDate(date) {
      return !!date && moment(date).isAfter(moment(), "day");
    },
    formatGuidanceDate(date) {
      return moment(date).format("MM-DD-YYYY");
    },
    orderAccessionDisabledReason(row) {
      // Mirror single-order checkCaseStatus guards; returns a reason string when
      // the order cannot be accessioned, or "" when it can.
      const excludedStatuses = ["COMPLETE", "COMPLETED", "ARCHIVED", "REJECTED"];
      if (excludedStatuses.includes(row.status)) {
        return `Accession not allowed for ${this.formatStatusText ? this.formatStatusText(row.status) : row.status} orders`;
      }
      if (
        [caseConstants.ORDER_TYPES.EMR, caseConstants.ORDER_TYPES.TESTGO, caseConstants.ORDER_TYPES.MRYODA].includes(row.order_type) &&
        row.sent_to_mlx &&
        (!row.collected_tests || !row.collected_tests.length)
      ) {
        return "Sent to MLX with but not performed";
      }
      return "";
    },
    isOrderAccessionable(row) {
      return !this.orderAccessionDisabledReason(row);
    },
    handleSelectionChange(rows) {
      if (this.bulkAccessionPopup) return;
      if (rows) {
        const unique = [...new Map(rows.map((r) => [String(r._id), r])).values()];
        this.selectedRows = unique;
        this.selectedCaseIds = unique.map((r) => r._id);
      } else {
        this.selectedRows = [];
        this.selectedCaseIds = [];
      }
    },
    async onBulkAccession() {
      // Fit the table to the viewport, leaving room for header, case types and footer.
      this.bulkTableMaxHeight = Math.max(280, window.innerHeight - 400);
      this.bulkInsuranceSwapping = {};
      this.orderTubeInfoMap = {};
      this.orderAckStatusMap = {};
      this.orderAckOriginalMap = {};
      this.bulkAckSaving = false;
      this.orderCaseTypeRowsMap = {};
      this.orderCaseTypeRowsLoading = {};
      this.selectedCaseTypeKeysMap = {};
      this.globalSelectedCaseTypeKeys = {};
      this.orderDatesMap = {};
      this.orderRowErrors = {};
      this.orderDeselectedTestsMap = {};
      this.bulkSelectedOrderIds = {};
      this.bulkNonGeneticDialog = false;
      this.bulkNonGeneticExistingDetails = [];
      this.bulkNonGeneticCaseType = [];
      this.bulkNonGeneticOrderData = {};
      this.bulkResultStatus = null;
      this.bulkResultMessage = "";
      this.orderVendorMap = {};
      this.orderLabMap = {};
      this.bulkCreatedCaseIds = [];
      this.bulkCreatedCCCaseIds = [];
      this.bulkCreatedOrderIds = [];
      this.tubeInfoFetching = true;
      const now = new Date().toISOString();
      const alreadyDoneByOrder = {};
      for (const row of this.selectedRows) {
        alreadyDoneByOrder[row._id] = (row.accession_data || []);
        this.orderDatesMap[row._id] = {
          collection_date: row.collection_date || null,
          received_date: now,
        };
        this.orderCaseTypeRowsLoading[row._id] = true;
      }
      this.bulkAccessionPopup = true;
      this.fetchAllSendoutLabs();
      const ids = this.selectedRows.map((row) => row._id);
      const bulkTubeInfoMap = await this.$store.dispatch("orders/fetchOrderTestsTubeInfoBulk", ids);
      for (const row of this.selectedRows) {
        const tubes = JSON.parse(JSON.stringify(bulkTubeInfoMap[row._id] || []));
        this.orderTubeInfoMap[row._id] = tubes;
        // Reuse the same tubes shown in the TUBES column for acknowledgment —
        // each tube's own status comes from its sample_acknowledgement entry
        // (matched by tube_name), falling back to the tube's own status field.
        const ackStatuses = {};
        const ackOriginals = {};
        const labs = {};
        tubes.forEach((tube) => {
          const ownEntry = (tube.sample_acknowledgement || []).find(
            (entry) => entry.tube_name === tube.tube_name
          );
          const rawStatus = ownEntry?.status || tube.status;
          const status = rawStatus === "RECEIVED" ? "RECEIVED" : "NOT_RECEIVED";
          ackStatuses[tube.tube_name] = status;
          ackOriginals[tube.tube_name] = status;
          labs[tube.tube_name] = tube.sendout_lab || "";
        });
        this.orderAckStatusMap[row._id] = ackStatuses;
        this.orderAckOriginalMap[row._id] = ackOriginals;
        this.orderLabMap[row._id] = labs;
        // Auto-select vendor from tube info if it exists
        if (tubes.length > 0 && tubes[0].vendor) {
          this.orderVendorMap[row._id] = tubes[0].vendor;
        }
        // Derive unique { case_type, is_sendout } rows from tube tests
        const seen = new Set();
        const ctRows = [];
        for (const tube of tubes) {
          for (const test of (tube.tests || [])) {
            const caseTypes = Array.isArray(test.case_type) ? test.case_type : (test.case_type ? [test.case_type] : []);
            for (const ct of caseTypes) {
              if (this.isOrderCaseTypeRejected(row, ct)) continue;
              const isSendout = test.is_sendout === true;
              const isAllergy = test.is_allergy === true;
              const key = `${ct}_${isAllergy ? "allergy" : isSendout ? "sendout" : "normal"}`;
              if (!seen.has(key)) {
                seen.add(key);
                ctRows.push({ case_type: ct, is_sendout: isSendout, is_allergy: isAllergy, key });
              }
            }
          }
        }
        // Fallback: use order case_types if tube info has no tests
        if (!ctRows.length) {
          for (const ct of (row.case_types || [])) {
            if (this.isOrderCaseTypeRejected(row, ct)) continue;
            ctRows.push({ case_type: ct, is_sendout: false, is_allergy: false, key: `${ct}_normal` });
          }
        }
        this.orderCaseTypeRowsMap[row._id] = ctRows;
        this.orderCaseTypeRowsLoading[row._id] = false;
      }
      // Build global case type selection: selected = true if any order has it unprocessed
      const globalKeys = {};
      for (const row of this.selectedRows) {
        for (const ctRow of (this.orderCaseTypeRowsMap[row._id] || [])) {
          if (this.isOrderCaseTypeRejected(row, ctRow.case_type)) continue;
          if (!(ctRow.key in globalKeys)) globalKeys[ctRow.key] = false;
          const isDone = alreadyDoneByOrder[row._id].some((a) => a.case_type === ctRow.case_type && !!(a.sendout_required) === !!ctRow.is_sendout && !!(a.is_allergy) === !!ctRow.is_allergy);
          if (!isDone) globalKeys[ctRow.key] = true;
        }
      }
      this.globalSelectedCaseTypeKeys = globalKeys;
      // Default all rows to selected
      const selectedIds = {};
      for (const row of this.selectedRows) {
        selectedIds[row._id] = true;
      }
      this.bulkSelectedOrderIds = selectedIds;
      this.tubeInfoFetching = false;
    },
    toggleGlobalCaseType(ctKey, val) {
      this.globalSelectedCaseTypeKeys = { ...this.globalSelectedCaseTypeKeys, [ctKey]: val };
    },
    selectAllGlobalCaseTypes(val) {
      const updated = { ...this.globalSelectedCaseTypeKeys };
      for (const ct of this.globalCaseTypeRows) {
        if (!ct.allDone) updated[ct.key] = val;
      }
      this.globalSelectedCaseTypeKeys = updated;
    },
    isBulkCaseTypeDone(row, ctRow) {
      return (row.accession_data || []).some(
        (a) => a.case_type === ctRow.case_type && !!a.sendout_required === !!ctRow.is_sendout && !!a.is_allergy === !!ctRow.is_allergy
      );
    },
    getInsuranceSwapAction,
    getOrderInsurance,
    // Swapping insurance never blocks Create Accessions; re-verification runs in the background.
    async onBulkSwapInsurance(row) {
      const action = getInsuranceSwapAction(row);
      const { primaryPayor, secondaryPayor } = getOrderInsurance(row);
      try {
        await ElMessageBox.confirm(
          action === "undo"
            ? `Swap back so ${secondaryPayor} is primary and ${primaryPayor} is secondary for ${row.order_id}?`
            : `Make ${secondaryPayor} primary and move ${primaryPayor} to secondary for ${row.order_id}?`,
          action === "undo" ? "Swap insurance back" : "Swap insurance",
          { confirmButtonText: action === "undo" ? "Swap Back" : "Swap", cancelButtonText: "Cancel", type: "warning" }
        );
      } catch (cancelled) {
        return;
      }

      this.bulkInsuranceSwapping = { ...this.bulkInsuranceSwapping, [row._id]: true };
      try {
        const result = await this.$store.dispatch("orders/swapOrderInsurance", { order_id: row._id });
        if (result?.success) {
          row.billing_info = result.data.billing_info;
          row.insurance_swapped_manually = result.data.insurance_swapped_manually;
          successNotification(result.message);
        } else {
          errorNotification(result?.message || "Failed to swap insurance");
        }
      } finally {
        this.bulkInsuranceSwapping = { ...this.bulkInsuranceSwapping, [row._id]: false };
      }
    },
    // INS badge for insurance orders; BILL badge with the billing type (Patient, Client, Sendout...) otherwise.
    bulkOrderBilling(row) {
      const billingType = row.billing_type || "";
      if (billingType === "INSURANCE") {
        const payor = row.billing_info?.insurance?.primary_insurance?.payor;
        return payor
          ? { kind: "insurance", label: "INS", name: payor, title: `Insurance: ${payor}` }
          : { kind: "missing", label: "INS", name: "No payor", title: "Insurance order without a primary payor" };
      }
      if (!billingType) {
        return { kind: "missing", label: "BILL", name: "Not set", title: "Billing type not set" };
      }
      const name = billingType.charAt(0) + billingType.slice(1).toLowerCase().replace(/_/g, " ");
      return { kind: "other", label: "BILL", name, title: `Billing: ${name}` };
    },
    toggleBulkOrderSelection(orderId, val) {
      this.bulkSelectedOrderIds = { ...this.bulkSelectedOrderIds, [orderId]: val };
    },
    toggleAllBulkOrders(val) {
      const updated = {};
      for (const row of this.selectedRows) {
        updated[row._id] = val;
      }
      this.bulkSelectedOrderIds = updated;
    },
    async openNonGeneticDialog(errorInfo) {
      this.bulkNonGeneticExistingDetails = errorInfo?.existed_details || [];
      this.bulkNonGeneticCaseType = errorInfo?.case_type ? [errorInfo.case_type] : [];
      const baseParams = errorInfo?.order_params || {};
      const orderId = baseParams.order;
      let enriched = { ...baseParams };
      if (orderId) {
        try {
          const orderData = await this.$store.dispatch("orders/fetchOrderDetails", orderId);
          if (orderData) {
            enriched = {
              ...enriched,
              hospital: orderData.hospital?._id || enriched.hospital,
              ordering_physician: orderData.ordering_physician?._id || enriched.ordering_physician,
              icd_10_codes: orderData.icd_10_codes || enriched.icd_10_codes,
              billing_type: orderData.billing_type || enriched.billing_type,
              patient_info: orderData.patient_info ? { ...orderData.patient_info } : enriched.patient_info,
              medication: orderData.medications?.length ? orderData.medications : (enriched.medication?.length ? enriched.medication : ["NOT AVAILABLE"]),
            };
          }
        } catch (e) {
          console.error("[BulkAccession] Failed to fetch full order for pending case:", e);
        }
      }
      this.bulkNonGeneticOrderData = enriched;
      this.bulkNonGeneticDialog = true;
    },
    /*
     * Compute the set of fully-rejected case types
     * for an order. A case is rejected only when
     * all of its test titles are rejected.
     *
     * Sources:
     *   - legacy samples_with_issues (CASE / TUBE)
     *   - new order_rejection_data (CASE / TUBE)
     *
     * The bulk tube-info is NOT available here, so
     * for order_rejection_data CASE-level entries
     * we trust that every title in the entry
     * belongs to the case it was registered against.
     */
    getRejectedCaseTypeSet(row) {
      const result = new Set();

      // Legacy samples_with_issues
      for (const issue of row?.samples_with_issues || []) {
        if (String(issue?.status || "").toUpperCase() !== "REJECTED") continue;
        const level = String(issue?.rejection_level || "").toUpperCase();
        if (level === "CASE" && issue.case_type) {
          result.add(issue.case_type);
        }
      }

      // New order_rejection_data — CASE level
      for (const rejection of row?.order_rejection_data || []) {
        const level = String(rejection?.rejection_level || "").toUpperCase();
        if (level !== "CASE") continue;
        // Title-based: we don't have tube info here, so we can't infer the
        // case type from the title alone. The case_type_label and case_type
        // are not stored on order_rejection_data (by design), so CASE-level
        // rejection visibility through this getter is intentionally limited.
        // The dialog and Create Accession handle the proper mapping using
        // the bulk tube info.
      }

      return result;
    },
    /*
     * Conservative rejection check used in lists/bulk
     * accession flow:
     *  - CASE-level rejection in legacy data ⇒ reject
     *  - TUBE-level rejection in legacy data that
     *    matches a tube of this case ⇒ reject
     *
     * order_rejection_data CASE/TUBE entries are
     * NOT used to skip whole case types here,
     * because title→case mapping requires the
     * bulk tube info. The reject dialog and Create
     * Accession apply the precise filtering with
     * tube info available.
     */
    isOrderCaseTypeRejected(row, caseType) {
      if (!caseType) return false;
      if (this.getRejectedCaseTypeSet(row).has(caseType)) return true;
      const tubes = this.orderTubeInfoMap[row?._id] || [];
      for (const issue of row?.samples_with_issues || []) {
        if (String(issue?.status || "").toUpperCase() !== "REJECTED") continue;
        if (String(issue?.rejection_level || "").toUpperCase() !== "TUBE") continue;
        if (!issue.tube_id) continue;
        const matchingTube = tubes.find(
          (t) => (t.tube_id || t._id) === issue.tube_id
        );
        if (!matchingTube) continue;
        const caseTests = (matchingTube.tests || []).filter((t) => {
          const cts = Array.isArray(t.case_type) ? t.case_type : (t.case_type ? [t.case_type] : []);
          return cts.includes(caseType);
        });
        if (caseTests.length) return true;
      }
      return false;
    },
    // A test is restricted (not orderable) when its lab-order code is blocked
    // for the current lab — colony labs (DLW-TX / DLW-HOU) block the full list,
    // the parent DLW lab blocks only DLW_UIBC. Uses the global helper set.
    isTestRestricted(test) {
      const code = test?.lab_order_code;
      return Boolean(code) && this.disabledLabOrderCodes.has(code);
    },
    isTestVisible(test) {
      const caseTypes = Array.isArray(test.case_type) ? test.case_type : (test.case_type ? [test.case_type] : []);
      const isSendout = test.is_sendout === true;
      const isAllergy = test.is_allergy === true;
      const suffix = isAllergy ? "allergy" : isSendout ? "sendout" : "normal";
      return caseTypes.some((ct) => !!this.globalSelectedCaseTypeKeys[`${ct}_${suffix}`]);
    },
    isTestDeselected(orderId, labOrderCode) {
      return !!(this.orderDeselectedTestsMap[orderId]?.[labOrderCode]);
    },
    toggleOrderTest(orderId, labOrderCode) {
      const current = this.orderDeselectedTestsMap[orderId]?.[labOrderCode] || false;
      this.orderDeselectedTestsMap = {
        ...this.orderDeselectedTestsMap,
        [orderId]: { ...(this.orderDeselectedTestsMap[orderId] || {}), [labOrderCode]: !current },
      };
    },
    updateOrderVendor(orderId, value) {
      this.orderVendorMap = {
        ...this.orderVendorMap,
        [orderId]: value || "",
      };
    },
    updateOrderLab(orderId, tubeName, value) {
      this.orderLabMap = {
        ...this.orderLabMap,
        [orderId]: { ...(this.orderLabMap[orderId] || {}), [tubeName]: value || "" },
      };
    },
    async fetchAllSendoutLabs() {
      if (this.sendoutLabsFetched || this.sendoutLabsFetching) return;
      this.sendoutLabsFetching = true;
      try {
        await this.$store.dispatch("labs/fetchAllLabs", {
          get_all: true,
          for_sendout: true,
        });
        this.sendoutLabsFetched = true;
      } finally {
        this.sendoutLabsFetching = false;
      }
    },
    // Titles of the tests currently selected for accession on this tube:
    // visible under the active case-type checkboxes, not manually
    // deselected, and not restricted (in-house at another lab).
    getSelectedOrderCodesForTube(orderId, tubeName) {
      const tubes = this.orderTubeInfoMap[orderId] || [];
      const titles = [];
      for (const tube of tubes) {
        if (tube.tube_name !== tubeName) continue;
        for (const test of (tube.tests || [])) {
          if (!test?.title) continue;
          if (!this.isTestVisible(test)) continue;
          if (this.isTestRestricted(test)) continue;
          if (this.isTestDeselected(orderId, test.lab_order_code)) continue;
          if (!titles.includes(test.title)) titles.push(test.title);
        }
      }
      return titles;
    },
    hasAckByInfo(tube) {
      return !!(tube?.acknowledged_by || tube?.acknowledged_at || tube?.lab);
    },
    updateOrderAckStatus(orderId, tubeName, value) {
      this.orderAckStatusMap = {
        ...this.orderAckStatusMap,
        [orderId]: { ...(this.orderAckStatusMap[orderId] || {}), [tubeName]: value },
      };
      // Clear any stale result banner so the unconfirmed-change warning is visible and not masked.
      if (this.bulkResultStatus !== "success") {
        this.bulkResultStatus = null;
        this.bulkResultMessage = "";
      }
    },
    async confirmBulkAcknowledgment() {
      const payload = this.bulkAckPayload;
      if (!payload.length) {
        errorNotification("No orders selected. Please select at least one order.");
        return;
      }
      try {
        this.bulkAckSaving = true;
        await this.$store.dispatch("orders/updateSampleAcknowledgement", { payload });
        successNotification("Sample acknowledgement updated successfully");
        // Fold the saved statuses into the "original" snapshot so the confirm
        // button goes back to disabled without reloading (would drop the
        // in-progress accession selections in this dialog).
        const updatedOriginals = { ...this.orderAckOriginalMap };
        payload.forEach((item) => {
          updatedOriginals[item.order_id] = {
            ...(updatedOriginals[item.order_id] || {}),
            [item.tube_name]: item.status,
          };
        });
        this.orderAckOriginalMap = updatedOriginals;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to update sample acknowledgement";
        errorNotification(message);
      } finally {
        this.bulkAckSaving = false;
      }
    },
    hasVisibleTests(orderId) {
      return (this.orderTubeInfoMap[orderId] || []).some((tube) =>
        (tube.tests || []).some((t) => this.isTestVisible(t))
      );
    },
    buildTubeInfoForCaseTypes(tubeInfoList, caseTypes, isSendout = false, deselectedCodes = {}, isAllergy = false, ackStatuses = {}) {
      const tubeMap = new Map();
      for (const tubeInfo of tubeInfoList) {
        // Skip tubes marked "Not Received" — their tests must not be sent for accessioning.
        if ((ackStatuses[tubeInfo.tube_name] || "NOT_RECEIVED") !== "RECEIVED") continue;
        const relevantTests = (tubeInfo.tests || []).filter((t) =>
          caseTypes.some((ct) => t.case_type?.includes(ct)) &&
          (t.is_sendout || false) === isSendout &&
          (t.is_allergy || false) === isAllergy &&
          !deselectedCodes[t.lab_order_code] &&
          !this.isTestRestricted(t)
        );
        if (!relevantTests.length) continue;
        const existing = tubeMap.get(tubeInfo.tube_id || tubeInfo.tube_name);
        if (existing) {
          relevantTests.forEach((t) => {
            if (!existing.collected_tests.includes(t.title)) {
              existing.collected_tests.push(t.title);
            }
          });
        } else {
          tubeMap.set(tubeInfo.tube_id || tubeInfo.tube_name, {
            tube_id: tubeInfo.tube_id,
            tube_name: tubeInfo.tube_name,
            image_url: tubeInfo.image_url || "",
            status: "Collected",
            collected_tests: relevantTests.map((t) => t.title),
          });
        }
      }
      return Array.from(tubeMap.values());
    },
    filteredPatient(patient) {
      const filtered = {};
      for (const key in patient) {
        if (key !== "addresses") {
          filtered[key] = patient[key];
        }
      }
      return filtered;
    },
    trimObjectStrings(obj) {
      if (Array.isArray(obj)) {
        return obj.map((item) => this.trimObjectStrings(item));
      } else if (obj !== null && typeof obj === "object") {
        const newObj = {};
        Object.keys(obj).forEach((key) => {
          newObj[key] = this.trimObjectStrings(obj[key]);
        });
        return newObj;
      } else if (typeof obj === "string") {
        return obj.trim();
      }
      return obj;
    },
    async confirmBulkAccession() {
      // Guard against creating accessions while a Received/Not Received change is unconfirmed.
      if (this.hasUnconfirmedAckChanges) {
        this.bulkResultStatus = "error";
        this.bulkResultMessage = "Please click \"Confirm Acknowledgment\" before creating accessions.";
        return;
      }
      this.bulkResultStatus = null;
      this.bulkResultMessage = "";
      this.orderRowErrors = {};
      this.bulkCreatedCaseIds = [];
      this.bulkCreatedCCCaseIds = [];
      this.bulkCreatedOrderIds = [];
      try {
        this.bulkAccessionLoading = true;
        let successCount = 0;
        let failCount = 0;
        const globalKeys = toRaw(this.globalSelectedCaseTypeKeys);
        const rowsToProcess = this.selectedRows.filter((r) => !!this.bulkSelectedOrderIds[r._id]);
        if (!rowsToProcess.length) {
          this.bulkResultStatus = "error";
          this.bulkResultMessage = "No orders selected. Please select at least one order.";
          return;
        }
        for (const row of rowsToProcess) {
          const allCtRows = toRaw(this.orderCaseTypeRowsMap[row._id] || []);
          const doneAccessions = (row.accession_data || []);
          const ctRowsToProcess = allCtRows.filter(
            (r) => !!globalKeys[r.key] && !this.isOrderCaseTypeRejected(row, r.case_type) && !doneAccessions.some((a) => a.case_type === r.case_type && !!(a.sendout_required) === !!r.is_sendout && !!(a.is_allergy) === !!r.is_allergy)
          );
          console.log(`[BulkAccession] Order ${row.order_id}: ${ctRowsToProcess.length} case type(s)`, ctRowsToProcess.map((r) => `${r.case_type}(sendout=${r.is_sendout},allergy=${r.is_allergy})`));
          for (const ctRow of ctRowsToProcess) {
            const caseType = String(ctRow.case_type);
            const ackStatuses = toRaw(this.orderAckStatusMap[row._id] || {});
            const tubeInformation = this.buildTubeInfoForCaseTypes(
              toRaw(this.orderTubeInfoMap[row._id] || []),
              [caseType],
              ctRow.is_sendout,
              toRaw(this.orderDeselectedTestsMap[row._id] || {}),
              ctRow.is_allergy || false,
              ackStatuses
            );
            if (!tubeInformation.length) {
              // All tubes for this case type are marked "Not Received" — skip accessioning it.
              failCount++;
              if (!this.orderRowErrors[row._id]) this.orderRowErrors[row._id] = {};
              this.orderRowErrors[row._id][ctRow.key] = {
                message: "Sample not received. Cannot create accession.",
                error_code: null,
                case_type: caseType,
              };
              continue;
            }
            const params = {
              lab: this.getUserPrimaryLabId,
              received_date: this.orderDatesMap[row._id]?.received_date || null,
              collection_date: this.orderDatesMap[row._id]?.collection_date || null,
              case_types: [caseType],
              order: row._id,
              billing_acknowledged: true,
              sendout_required: ctRow.is_sendout,
              is_allergy: ctRow.is_allergy || false,
              test_tube_information: tubeInformation,
              send_to_phlebio: false,
              patient_info: this.filteredPatient(row.patient_info || {}),
              billing_info: this.trimObjectStrings(row.billing_info || {}),
              is_bulk: true,
              vendor: this.orderVendorMap[row._id] || undefined,
            };
            await this.$store.dispatch("orders/createAccessionForOrder", params);
            if (this.getAccessionCreateStatus) {
              successCount++;
              const createdCaseId = this.getCreateAccessionDetails?._id || this.getCreateAccessionDetails?.accession_id;
              if (createdCaseId && createdCaseId !== true) {
                if (caseType === 'CLINICAL_CHEMISTRY') {
                  this.bulkCreatedCCCaseIds.push(createdCaseId);
                } else {
                  this.bulkCreatedCaseIds.push(createdCaseId);
                }
              }
              if (!this.bulkCreatedOrderIds.includes(row._id)) {
                this.bulkCreatedOrderIds.push(row._id);
              }
              // Mark this case type as done on the row so the tag updates to "Done"
              if (!row.accession_data) row.accession_data = [];
              row.accession_data.push({ case_type: caseType, sendout_required: ctRow.is_sendout, is_allergy: ctRow.is_allergy || false });
              // If all orders with this case type are now done, deselect it globally
              const allNowDone = this.selectedRows.every((r) => {
                const has = (this.orderCaseTypeRowsMap[r._id] || []).some((rc) => rc.key === ctRow.key);
                return !has || (r.accession_data || []).some((a) => a.case_type === caseType && !!(a.sendout_required) === !!ctRow.is_sendout && !!(a.is_allergy) === !!ctRow.is_allergy);
              });
              if (allNowDone) {
                this.globalSelectedCaseTypeKeys = { ...this.globalSelectedCaseTypeKeys, [ctRow.key]: false };
              }
            } else {
              failCount++;
              const errData = this.getAccessionCreationValidationErrorCode;
              if (!this.orderRowErrors[row._id]) this.orderRowErrors[row._id] = {};
              this.orderRowErrors[row._id][ctRow.key] = {
                message: errData?.message || "Failed to create accession",
                error_code: errData?.error_code || null,
                existed_details: errData?.errors?.existed_details || null,
                case_type: caseType,
                order_params: params,
              };
            }
          }
        }
        if (failCount > 0 && successCount === 0) {
          this.bulkResultStatus = "error";
          this.bulkResultMessage = `All ${failCount} accession(s) failed. Please check the details and try again.`;
        } else if (failCount > 0) {
          this.bulkResultStatus = "partial";
          this.bulkResultMessage = `${successCount} accession(s) created. ${failCount} failed.`;
        } else if (successCount > 0) {
          this.bulkResultStatus = "success";
          this.bulkResultMessage = `${successCount} accession(s) created successfully.`;
        } else {
          this.bulkResultStatus = "error";
          this.bulkResultMessage = "No case types selected to process.";
        }
      } catch (error) {
        this.bulkResultStatus = "error";
        this.bulkResultMessage = "Unexpected error while creating accessions.";
        console.log(error);
      } finally {
        this.bulkAccessionLoading = false;
      }
    },
    onBulkPrintBarCodes() {
      const urlData = this.$router.resolve({
        name: "MultipleBarcodes",
        path: "multiple-barcodes",
        query: { case_ids: this.bulkCreatedCaseIds },
      });
      window.open(urlData.href, "_blank");
    },
    onBulkPrintOrderBarCodes() {
      const urlData = this.$router.resolve({
        name: "TestGOOrderBarcode",
        path: "/testgo-order-barcodes",
        query: { order_ids: this.bulkCreatedOrderIds },
      });
      window.open(urlData.href, "_blank");
    },
    onBulkPrintTestBarCodes() {
      const urlData = this.$router.resolve({
        name: "MultipleTestBarcodes",
        path: "multiple-test-barcodes",
        query: { case_ids: this.bulkCreatedCCCaseIds },
      });
      window.open(urlData.href, "_blank");
    },
    closeBulkAccessionPopup() {
      this.bulkAccessionPopup = false;
      this.selectedCaseIds = [];
      this.selectedRows = [];
      if (this.bulkResultStatus) this.fetchAllOrders();
    },
    onPrintBarCodes() {
      let urlData = this.$router.resolve({
        name: "TestGOOrderBarcode",
        path: "/testgo-order-barcodes",
        query: {
          order_ids: this.selectedCaseIds,
        },
      });
      window.open(urlData.href, "_blank");
    },
    async fetchAllUsers() {
      this.userLoading = true;
      const result = await this.$store.dispatch("userAccounts/fetchAllUserAccounts", {
        get_all: true,
        user_types: ["MARKETER","HOSPITAL_MARKETING_MANAGER","SALES_DIRECTOR","REGIONAL_DIRECTOR","SALES_SUPPORT_TEAM"],
        order_by: "first_name",
        order_type: "asc",
        status: "ACTIVE",
      });
      if (result && result.data) {
        this.marketerData = result.data;
      }
      this.userLoading = false;
    },
    async fetchAllTechnicians() {
      if (this.technicianData.length) return;
      this.technicianLoading = true;
      const data = await this.$store.dispatch("orders/fetchTechnicianNames");
      if (Array.isArray(data)) {
        this.technicianData = data;
      } else if (data && Array.isArray(data.data)) {
        this.technicianData = data.data;
      }
      this.technicianLoading = false;
    },
    async onDelete(id) {
      this.requestArchiveOrder = id;
      this.takeReasonDialog = true;
    },
    async deleteCase(reason) {
      try {
        await this.$store.dispatch("orders/deleteCase", {
          order_id: this.requestArchiveOrder,
          reason: reason,
        });
        if (this.getCaseDeleteStatus) {
          successNotification("Order deleted successfully");
          this.requestArchiveOrder = "";
          this.fetchAllOrders(this.page);
        } else {
          errorNotification(
            this.getCaseDeleteError || "Error while deleting order"
          );
        }
      } catch (error) {
        console.log(error);
      }
    },
    async updateScroll() {
      this.scrollPosition = window.scrollY;
    },
    async onReportsExport() {
      this.exportReportsDialogVisisble = true;
    },
    clearSearchBy() {
      if (
        this.$route.query.selectedOrderSearchBy === "Pregnancy" ||
        this.$route.query.selectedOrderSearchBy === "Future Orders"
      ) {
        this.fetchAllOrders(1);
      }
    },
    clearAllFilters() {
      this.search_string = "";
      this.patient_name = "";
      this.selectedHospital = [];
      this.hospital = "";
      this.physician = [];
      this.date_of_birth = "";
      this.caseType = [];
      this.hospital_marketer = [];
      this.priority = "";
      this.testgo_order_type = [];
      this.technician_name = [];
      this.acknowledgementFilter = "";
      this.showPartialOnly = null;
      this.partialReasonFilter = [];
      this.sentToMlxFilter = "";
      this.fromDate = "";
      this.toDate = "";
      this.page = 1;
      this.fetchAllOrders(1);
    },
    clearInputSearch() {
      if (
        this.hospital ||
        this.physician ||
        this.date_of_birth ||
        this.patient_name ||
        this.search_string ||
        this.caseType ||
        this.hospital_marketer.length ||
        this.patient_name ||
        this.priority ||
        this.fromDate ||
        this.toDate ||
        this.technician_name ||
        this.collectedFromDate ||
        this.collectedToDate
      ) {
        this.hospital = "";
        this.selectedHospital = [];
        this.hospitalsData.data = [];
        this.hospital_marketer = [];
        this.patient_name = "";
        this.physician = [];
        this.date_of_birth = "";
        this.caseType = [];
        this.patient_name = "";
        this.search_string = "";
        this.isSearchByInput = "";
        this.patient_name = "";
        this.technician_name = [];
        this.page = 1;
        (this.priority = ""),
          (this.fromDate = ""),
          (this.toDate = ""),
          (this.collectedFromDate = ""),
          (this.collectedToDate = ""),
          (this.acknowledgementFilter = ""),
          (this.sentToMlxFilter = ""),
          this.fetchAllOrders(1);
      }
      if (this.searchBy === "Pregnancy") {
        this.fetchAllOrders(1);
      }
      if (this.searchBy === "Future Orders") {
        this.fetchAllOrders(1);
      }
    },
    async fetchAllPhysicians(physician) {
      try {
        this.physiciansLoading = true;
        let params = {
          get_all: true,
        };
        if (physician && typeof physician === 'string') {
          params.search_string = physician;
        }
        if (Array.isArray(this.selectedHospital) && this.selectedHospital.length === 1) {
          params.hospital = this.selectedHospital[0]._id;
        }
        await this.$store.dispatch("physicians/fetchAllPhysicians", params);
        this.physiciansData = JSON.parse(JSON.stringify(this.getAllPhysicians));
        this.physiciansLoading = false;
      } catch (error) {
        console.log(error);
      }
    },
    async fetchAllHospitals(hospital) {
      try {
        this.hospitalsLoading = true;
        let params = {
          get_all: true,
          status: "ACTIVE",
        };
        if (hospital && typeof hospital === 'string') {
          params.search_string = hospital;
        }
        await this.$store.dispatch("hospitals/fetchAllHospitals", params);
        this.hospitalsData = JSON.parse(JSON.stringify(this.getAllHospitals));
        this.hospitalsLoading = false;
      } catch (error) {
        console.log(error);
      }
    },
    setQueryParamsData() {
      this.page = this.$route.query.selectedOrderPage
        ? parseInt(this.$route.query.selectedOrderPage)
        : 1;
      this.pageSize = this.$route.query.selectedOrderPageSize
        ? parseInt(this.$route.query.selectedOrderPageSize)
        : 25;
      if (this.$route.query.selectedOrderString) {
        this.search_string = this.$route.query.selectedOrderString;
      }
      if (this.$route.query.selectedOrderPatientName) {
        this.patient_name = this.$route.query.selectedOrderPatientName;
      }
      if (this.$route.query.is_stat) {
        this.priority = this.$route.query.is_stat;
      }
      if (this.$route.query.fromDate) {
        this.fromDate = this.$route.query.fromDate;
      }
      if (this.$route.query.toDate) {
        this.toDate = this.$route.query.toDate;
      }
      if (this.$route.query.selectedOrderCase) {
        const sel = this.$route.query.selectedOrderCase;
        this.caseType = Array.isArray(sel) ? sel : [sel];
      }
      if (this.$route.query.selectedCaseId) {
        this.selectedCaseId = this.$route.query.selectedCaseId;
      }
      if (this.$route.query.selectedOrderSearchBy) {
        this.searchBy = this.$route.query.selectedOrderSearchBy;
      }
      if (this.$route.query.selectedOrderPhysician) {
        const savedQuery = localStorage.getItem("searchQueryPhysician");
        if (
          savedQuery &&
          this.$route.query.selectedOrderString !== savedQuery
        ) {
          this.fetchAllPhysicians(savedQuery);
        }
        const selPhys = this.$route.query.selectedOrderPhysician;
        this.physician = Array.isArray(selPhys) ? selPhys : [selPhys];
      }
      if (this.$route.query.selectedOrderHospital) {
        const savedQuery = localStorage.getItem("searchQueryFacility");
        if (savedQuery && this.$route.query.selectedString !== savedQuery) {
          this.fetchAllHospitals(savedQuery);
        }
        const rawIds = this.$route.query.selectedOrderHospital;
        const hospitalIds = Array.isArray(rawIds) ? rawIds : [rawIds];
        const rawNames = this.$route.query.selectedOrderHospitalName;
        const hospitalNames = Array.isArray(rawNames) ? rawNames : [rawNames];
        this.hospital = hospitalIds[0] || "";
        const existing =
          this.hospitalsData && Array.isArray(this.hospitalsData.data)
            ? this.hospitalsData.data
            : [];
        this.selectedHospital = hospitalIds.map((id, i) => {
          const found = existing.find((h) => h._id === id);
          return found || { _id: id, name: hospitalNames[i] || "" };
        });
        const missing = this.selectedHospital.filter(
          (h) => !existing.some((e) => e._id === h._id)
        );
        if (missing.length) {
          this.hospitalsData.data = [...existing, ...missing];
        }
      }
      if (this.$route.query.selectedMarketer) {
        this.fetchAllUsers();
        this.hospital_marketer = Array.isArray(this.$route.query.selectedMarketer)
          ? this.$route.query.selectedMarketer
          : [this.$route.query.selectedMarketer];
      }
      if (this.$route.query.selectedOrderDateOfBirth) {
        this.date_of_birth = this.$route.query.selectedOrderDateOfBirth;
      }
      if (this.$route.query.selectedOrderOrderBy) {
        this.orderBy = this.$route.query.selectedOrderOrderBy;
      }
      if (this.$route.query.selectedOrderOrderType) {
        this.orderType = this.$route.query.selectedOrderOrderType;
      }
      if (this.$route.query.selectedOrderPlatform) {
        const selPlatform = this.$route.query.selectedOrderPlatform;
        this.testgo_order_type = Array.isArray(selPlatform)
          ? selPlatform
          : [selPlatform];
      }
      if (this.$route.query.selectedTechnicianName) {
        this.fetchAllTechnicians();
        const selTech = this.$route.query.selectedTechnicianName;
        this.technician_name = Array.isArray(selTech) ? selTech : [selTech];
      }
    },
    prepareParams() {
      this.pregnant = "";
      this.featureDateOrders = "";
      const params = {
        page: this.page,
        limit: this.pageSize,
        is_validation_purpose: this.validationStatus ? true : false,
        sample_ack_tab: this.sampleAckTab ? true : false,
      };
      if (this.insuranceOnHold) {
        params.insurance_on_hold = true;
      }
      if (this.samplePending) {
        params.sample_pending = true;
      }
      if (this.issues === true) {
        params.issues = true;
      }
      if (this.collectedOrder === true) {
        params.collected_order = true;
      }
      if (this.search_string) {
        params.search_string = this.search_string;
      }
      if (this.patient_name) {
        params.patient_name = this.patient_name;
      }
      if (this.date_of_birth) {
        const date_of_birth = moment(this.date_of_birth).format("YYYY-MM-DD");
        params.date_of_birth = date_of_birth;
      }
      if (Array.isArray(this.selectedHospital) && this.selectedHospital.length) {
        params.hospitals = this.selectedHospital
          .map((h) => h && h._id)
          .filter(Boolean);
      }
      if (this.hospital_marketer.length) {
        params.hospital_marketers = this.hospital_marketer;
      }
      if (this.caseType && this.caseType.length) {
        params.case_type = this.caseType;
      }
      if (this.physician && this.physician.length) {
        params.ordering_physician = this.physician;
      }
      if (this.searchBy === "Pregnancy") {
        this.pregnant = true;
        params.pregnant = true;
      }
      if (this.fromDate) {
        params.from_date = this.fromDate;
      }
      if (this.toDate) {
        params.to_date = this.toDate;
      }
      if ((this.fromDate || this.toDate) && this.dateType) {
        params.date_type = this.dateType;
      }
      if (this.collectedFromDate) {
        params.order_place_at_from = this.collectedFromDate;
      }
      if (this.collectedToDate) {
        params.order_place_at_to = this.collectedToDate;
      }
      if (this.priority === "Stat") {
        params.is_stat = true;
      }
      if (this.priority === "Routine") {
        params.is_stat = false;
      }
      if (this.orderBy && this.orderType) {
        params.order_by = this.orderBy;
        params.order_type = this.orderType;
      }
      if (this.searchBy === "Future Orders") {
        this.featureDateOrders = true;
        params.future_collection_date = true;
      }
      if (this.testgo_order_type && this.testgo_order_type.length) {
        params.order_type = this.testgo_order_type
      }
      if (this.technician_name && this.technician_name.length) {
        params.technician_name = this.technician_name;
      }
      if (this.acknowledgementFilter === "YES") {
        params.collected_order = true;
      } else if (this.acknowledgementFilter === "NO") {
        params.collected_order = false;
      }
      if (this.sentToMlxFilter === "YES") {
        params.sent_to_mlx = true;
      } else if (this.sentToMlxFilter === "NO") {
        params.sent_to_mlx = false;
      }
      params.status = this.showPartialOnly === true ? ["PARTIAL"] : this.selectedStatuses;
      if (this.isGowthamUser) {
        params.is_old = true;
      }
      return params;
    },
    updateQueryParams(params) {
      let selectedParams = {
        selectedOrderPage: params.page,
        selectedOrderCase: params.case_type,
        selectedOrderHospital: params.hospitals,
        selectedOrderHospitalName: Array.isArray(this.selectedHospital)
          ? this.selectedHospital.map((h) => (h && h.name) || "")
          : undefined,
        selectedMarketer: params.hospital_marketers,
        selectedOrderDateOfBirth: params.date_of_birth,
        selectedOrderPhysician: params.ordering_physician,
        selectedOrderPlatform: this.testgo_order_type,
        selectedOrderString: params.search_string,
        selectedOrderPatientName: params.patient_name,
        selectedOrderPageSize: params.limit,
        selectedOrderOrderBy: params.order_by,
        selectedOrderOrderType: params.order_type,
        selectedStatus: params.status,
        selectedCaseId: this.$route.query.selectedCaseId,
        selected_panel: this.$route.query.selected_panel,
        selectedOrderSearchBy: this.searchBy || undefined,
        selectedTest: params.tests_info,
        is_stat: this.priority,
        fromDate: params.from_date,
        toDate: params.to_date,
        selectedTechnicianName: params.technician_name,
      };
      this.updateQueryParam(this.$route.name, selectedParams);
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchAllOrders();
    },
    handleCurrentChange(page) {
      this.page = page;
      this.fetchAllOrders();
    },
    onDateTypeChange() {
      // Persist the choice as a filter preference (localStorage + profile).
      this.saveFilterPreferences();
      // Only re-query if a date range is active; otherwise just remember the choice.
      if (this.fromDate || this.toDate) {
        this.handleCurrentChange(1);
      }
    },
    // Range guards: no future dates, and From/To can't cross each other.
    disabledFromDate(date) {
      if (date > new Date()) return true;
      if (this.toDate) return moment(date).isAfter(moment(this.toDate), "day");
      return false;
    },
    disabledToDate(date) {
      if (date > new Date()) return true;
      if (this.fromDate) return moment(date).isBefore(moment(this.fromDate), "day");
      return false;
    },
    onFromDateChange() {
      // If the current To Date is now earlier than From, clear it to avoid an invalid range.
      if (
        this.fromDate &&
        this.toDate &&
        moment(this.toDate).isBefore(moment(this.fromDate), "day")
      ) {
        this.toDate = "";
      }
      this.handleCurrentChange(1);
    },
    handleCurrentChangeCaseType(page) {
      this.page = page;
      this.fetchAllOrders();
    },
    clearTest() {
      this.caseType = [];
      this.fetchAllOrders();
    },
    onChangeSort({ order, prop }) {
      if (order && prop) {
        this.orderBy = prop;
        this.orderType = order == "ascending" ? "asc" : "desc";
      } else {
        this.orderBy = "";
        this.orderType = "";
      }
      this.fetchAllOrders();
    },
    getAutoCompleteOrders() {
      // Debounce search input to avoid excessive API calls
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.fetchAllOrders();
      }, 600);
    },
    async fetchAllOrders() {
      // Guard against out-of-order responses: capture a sequence id for this
      // request and ignore its result if a newer request has been issued since.
      // Prevents a slow, stale search from overwriting the results and URL for
      // a newer one.
      const requestSeq = ++this.fetchSeq;
      try {
        this.loading = true;
        let params = this.prepareParams();
        await this.$store.dispatch("orders/getOrders", {
          ...params,
          __requestSeq: requestSeq,
        });
        if (requestSeq !== this.fetchSeq) return;
        this.updateQueryParams(params);
        // Optimize: Use map instead of forEach + push for better performance
        if (this.getAllOrders?.data?.length) {
          this.orderIds = this.getAllOrders.data.map((obj) => obj._id);
          // Clear caches when new data is loaded to prevent stale data
          this.clearFormattingCaches();
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        if (requestSeq === this.fetchSeq) this.loading = false;
      }
    },
    // Clear formatting caches when data changes
    clearFormattingCaches() {
      // Clear Map cache (for primitive values like status strings)
      this.statusTextCache.clear();
      // WeakMap caches will automatically handle garbage collection when rows are replaced
      // No need to manually clear WeakMaps as they use object references
    },
    // goToNotificationOrderView(orderId) {
    //   this.$router.push({
    //     name: "Order-Information",
    //     params: { order_id: orderId },
    //     query: {
    //       ...this.$route.query,
    //       testInfo: undefined,
    //       address: undefined,
    //       insurance: undefined,
    //       redirectRoute: this.$route.name,
    //       drawer: true,
    //     },
    //   });
    // },
    goToOrdersView(orderId) {
      this.$router.push({
        name: "Order-Information",
        params: { order_id: orderId },
        query: {
          ...this.$route.query,
          testInfo: undefined,
          address: undefined,
          insurance: undefined,
          redirectRoute: this.$route.name,
        },
      });
    },
    goToCaseView(case_id) {
      this.$router.push({
        name: "CaseView",
        params: { case_id: case_id },
        query: { ...this.$route.query, redirectRoute: this.$route.name },
      });
    },
    reOrderCase() {
      this.$router.push({
        name: "Re-Order",
        params: { order_id: this.reOrderId },
        query: {
          ...this.$route.query,
          testInfo: this.reOrderWithTestInfo,
          insurance: this.reOrderWithInsurance,
          address: this.reOrderWithAddress,
          redirectRoute: this.$route.name,
        },
      });
    },
    // Optimized handler to avoid inline function creation
    handleStatusChange(command, OrderData, index) {
      this.changeStatus(command, OrderData, index);
    },
    async onPartialRejectOption() {
      const selected = this.rejectOptionsContext;

      if (!selected?.row) {
        return;
      }

      this.showRejectOptionsPopup = false;

      await this.openOrderRejectDialog(selected.row);
    },
    async submitCompleteRejection(data) {
      try {
        const selected = this.rejectingOrderForComplete;

        if (!selected?.row) {
          return;
        }

        const targetStatus =
          this.rejectActionType === "activate" ? "ACTIVE" : "REJECTED";

        let params = {
          id: selected.row._id,
          status: targetStatus,
        };

        if (data?.notes) {
          params.notes = data.notes;
        }

        if (data?.reasons?.length) {
          params.reasons = data.reasons;
        }

        if (data?.image) {
          const formData = new FormData();

          formData.append("status", targetStatus);

          if (data.notes) {
            formData.append("notes", data.notes);
          }

          if (data.reasons?.length) {
            formData.append(
              "reasons",
              JSON.stringify(data.reasons)
            );
          }

          formData.append("image", data.image);

          params = {
            id: selected.row._id,
            formData,
          };
        }

        await this.updateStatus(params, selected.index);

        this.takeStatusMovingReasonDialog = false;
        this.rejectingOrderForComplete = null;
        this.rejectActionType = "reject";
        this.rejectOptionsContext = {
          row: null,
          index: null,
        };
      } catch (error) {
        console.error(
          "Error completing order rejection:",
          error
        );

        errorNotification(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to reject order"
        );
      }
    },
    closeRejectOptionsPopup() {
      this.showRejectOptionsPopup = false;

      this.rejectOptionsContext = {
        row: null,
        index: null,
      };
    },
    async onCompleteRejectOption() {
      const selected = this.rejectOptionsContext;
      if (!selected?.row) {
        return;
      }
      this.showRejectOptionsPopup = false;
      this.rejectingOrderForComplete = {
        row: selected.row,
        index: selected.index,
      };
      this.takeStatusChangeReasonDialogTitle = "Reason for Rejection";
      this.rejectActionType = "reject";
      this.takeStatusMovingReasonDialog = true;
    },
    async onPartialActivateOption() {
      const selected = this.rejectOptionsContext;
      if (!selected?.row) {
        return;
      }
      this.showRejectOptionsPopup = false;

      this.rejectingOrderForComplete = null;
      this.rejectActionType = "reject";

      await this.openOrderRejectDialog(selected.row, "activate");
    },
    async changeStatus(command, OrderData, index) {
      try {
      if (command === OrderData.status) {
        return;
      }

      if (command === "REJECTED") {
        // Already PARTIAL → directly open partial rejection
        if (OrderData.status === "PARTIAL") {
          await this.openOrderRejectDialog(OrderData);
          return;
        }

        // PARTIALLY_REJECTED → ask whether partial activation or partial rejection
        if (OrderData.status === "PARTIALLY_REJECTED") {
          this.rejectOptionsContext = {
            row: OrderData,
            index,
          };
          this.rejectOptionsMode = "reject";
          this.showRejectOptionsPopup = true;
          return;
        }

        // ACTIVE → ask whether partial or complete rejection
        if (OrderData.status === "ACTIVE") {
          this.rejectOptionsContext = {
            row: OrderData,
            index,
          };
          this.rejectOptionsMode = "reject";
          this.showRejectOptionsPopup = true;
          return;
        }
      }

      if (
        command === "ACTIVE" &&
        OrderData.status === "PARTIALLY_REJECTED"
      ) {
        await this.openOrderRejectDialog(OrderData, "activate");
        return;
      }

      this.takeStatusChangeReasonDialogTitle = command;

      const params = {
        status: command,
        id: OrderData._id,
      };

      if (
        OrderData.status === "ACTIVE" ||
        OrderData.status === "REJECTED"
      ) {
        this.unsetCompleteStatusChangeCase = OrderData._id;
        this.toStatus = command;
        this.caseIndex = index;

        this.askReasonOnUnsetCompletedStatus();
      } else {
        await this.updateStatus(params, index);
      }
      } catch (err) {
        console.error("Error changing status:", err);
        errorNotification("Failed to change order status");
      }
    },
    async unsetOrderCompleteStatus(data) {
      let params = {
        id: this.unsetCompleteStatusChangeCase,
        status: this.toStatus,
      };
      if (data.notes && data.notes !== "") {
        params.notes = data.notes;
      }
      if (data.reasons && data.reasons.length > 0) {
        params.reasons = data.reasons;
      }
      if (data.image) {
        const formData = new FormData();
        formData.append("status", params.status);
        if (params.notes) formData.append("notes", params.notes);
        if (params.reasons && params.reasons.length > 0) {
          formData.append("reasons", JSON.stringify(params.reasons));
        }
        formData.append("image", data.image);
        params = { id: params.id, formData };
      }
      await this.updateStatus(params, this.caseIndex);
    },

    async askReasonOnUnsetCompletedStatus() {
      this.takeStatusChangeReasonDialog = true;
    },
    async updateStatus(params) {
      try {
        this.loading = true;
        await this.$store.dispatch("orders/changeOrderStatus", params, {
          root: true,
        });
        if (this.getUpdateOrderStatus && this.getUpdateOrderStatus.success) {
          this.$notify({
            type: "success",
            title: "Success",
            message: this.getUpdateOrderStatus.message,
          });
          await this.$store.dispatch("orders/fetchOrderStats");

          await this.fetchAllOrders();
        }
        this.loading = false;
      } catch (err) {
        console.log(err);
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.order-id-wrap {
  position: relative;
  display: inline-block;
  padding-top: 10px;
  .platform-corner-badge {
    position: absolute;
    top: -2px;
    left: -8px;
    padding: 0 5px;
    border-radius: 8px;
    border: 1px solid #fff;
    font-size: 9px;
    font-weight: 600;
    line-height: 14px;
    white-space: nowrap;
    color: #fff;
    background: #909399;
  }
  .platform-emr {
    background: #409eff;
  }
  .platform-medical-group {
    background: #67c23a;
  }
  .platform-mlx {
    background: #e6a23c;
  }
  .platform-testgo {
    background: #909399;
  }
}
.stat-badge {
  margin-left: 6px;
  color: #1b2459;
  font-weight: 600;
  font-size: 12px;
}
.conversion-guidance {
  display: flex;
  flex-direction: column;
  gap: 3px;
  .conversion-guidance-row {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: help;
    min-width: 0;
    font-size: 12px;
    line-height: 1.4;
  }
  .conversion-status-icon {
    flex: none;
    font-size: 14px;
    &.status-ok {
      color: #67c23a;
    }
    &.status-redirect {
      color: #e6a23c;
      font-weight: 700;
    }
    &.status-block {
      color: #f56c6c;
    }
  }
  .conversion-case-type {
    flex: none;
    max-width: 70px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #8a8fa8;
    font-size: 11px;
  }
  .conversion-guidance-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    &.status-ok {
      color: #529b2e;
    }
    &.status-redirect {
      color: #b88230;
    }
    &.status-block {
      color: #c45656;
    }
  }
}
.conversion-guidance-tip {
  max-width: 360px;
  line-height: 1.5;
  .tip-title {
    font-weight: 600;
    margin-bottom: 4px;
  }
  .tip-other-labs {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }
  .tip-subtitle {
    font-weight: 600;
    margin-bottom: 2px;
  }
}
/* Keep the whole top toolbar on a single line: the left cluster sizes to its
   content and the right cluster takes the remaining width, so the action
   buttons never wrap and the search inputs never overflow into the date
   pickers (mirrors the All Cases toolbar). */
.listings-navbar {
  :deep(.el-row) {
    flex-wrap: nowrap;
  }
  .toolbar-left {
    flex: 0 1 auto;
    width: auto;
    max-width: 100%;
  }
  .toolbar-right {
    flex: 1 1 0;
    width: auto;
    max-width: none;
    min-width: 0;
  }
  /* Fixed gap between the SCAN QR CODE search and the first date picker. */
  .toolbar-right .list-items-group-15 > li:first-child {
    margin-left: 24px;
  }
  .barcode-print,
  .toolbar-right .list-items-group-15 {
    flex-wrap: nowrap;
  }
  /* Give the search inputs a fixed width so the left cluster stays compact. */
  .toolbar-left .search-input-lg {
    width: 175px;
    flex: 0 0 175px;
  }
  /* Shorten the SCAN QR CODE input width. */
  .toolbar-left .search-input-lg.scan-qr-input {
    width: 140px;
    flex: 0 0 140px;
  }
  /* Shorten the GENERATE BARCODE button width by tightening its padding. */
  .toolbar-left .generate-barcode-btn {
    padding-left: 10px;
    padding-right: 10px;
  }
  /* Slim the date pickers (Patient DOB / From / To) so the right cluster fits. */
  .toolbar-right .search-with-date-input {
    width: 120px;
  }
  /* Tighten spacing between the right-side controls. */
  .toolbar-right .list-items-group-15 > li:not(:last-child) {
    margin-right: 8px;
  }

  /* Narrow screens can't fit everything on one line — fall back to wrapping. */
  @media screen and (max-width: 1400px) {
    :deep(.el-row) {
      flex-wrap: wrap;
    }
    .toolbar-left,
    .toolbar-right {
      flex: 1 1 100%;
    }
    .barcode-print,
    .toolbar-right .list-items-group-15 {
      flex-wrap: wrap;
      row-gap: 8px;
    }
  }
}

/* Distribute the customizable filters evenly across a single line (they share
   the row width instead of being fixed 170px chips that orphan onto row 2). */
.filters-draggable {
  flex-wrap: nowrap;
  .filter-drag-item {
    flex: 1 1 0;
    min-width: 0;
  }
  :deep(.search-with-filter-input) {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    .el-select {
      width: 100%;
    }
  }
  @media screen and (max-width: 1400px) {
    flex-wrap: wrap;
    row-gap: 8px;
  }
}
.filter-toggle-btn.el-button {
  width: 100px !important;
  min-width: 100px !important;
  max-width: 100px !important;
  height: 35px !important;
  min-height: 35px !important;
  max-height: 35px !important;
  padding: 0 8px !important;
  margin: 0;
  border-radius: 6.4px !important;
  border: 1px solid #dedbdb;
  background: #fff;
  color: #273480;
  cursor: pointer;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s ease;
  &:hover,
  &:focus {
    background: #273480;
    border-color: #273480;
    color: #fff;
  }
}
.slide-filters-enter-active,
.slide-filters-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
  max-height: 200px;
  opacity: 1;
}
.slide-filters-enter-from,
.slide-filters-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0 !important;
}
</style>
<style lang="scss">
.payor-mapped-row td {
  background-color: #e6f4ea !important;
}

.tube-badge {
  display: inline;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 500;
  color: #555;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 10px;
  white-space: nowrap;
}

/* Bulk Accession Dialog (teleported to body, so these styles must stay unscoped) */
$ba-navy: #273480;
$ba-navy-soft: #eef0fa;
$ba-text: #1f2937;
$ba-muted: #6b7280;
$ba-faint: #9ca3af;
$ba-line: #e5e7eb;
$ba-surface: #f8f9fc;
$ba-green: #15803d;
$ba-green-soft: #ecfdf3;
$ba-red: #b91c1c;
$ba-red-soft: #fef2f2;
$ba-amber: #b45309;
$ba-amber-soft: #fffbeb;
$ba-blue: #1d5fb8;
$ba-blue-soft: #eef5ff;

.el-dialog.bulk-accession-dialog {
  max-width: 1600px;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(17, 24, 39, 0.22);

  .el-dialog__body {
    padding: 0 !important;
  }
  .el-dialog__footer {
    padding: 0 !important;
  }
}

.ba-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px 14px;
  border-bottom: 1px solid $ba-line;
  background: #fff;

  .ba-title {
    font-size: 18px;
    font-weight: 700;
    color: $ba-text;
    line-height: 1.3;
  }
  .ba-subtitle {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 4px;
    font-size: 13px;
    color: $ba-muted;

    strong {
      color: $ba-navy;
      font-weight: 700;
    }
  }
  .ba-stat-sep {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #d1d5db;
  }
  .ba-stat-loading {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: $ba-faint;
  }
  .ba-close {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: $ba-muted;
    font-size: 15px;
    line-height: 32px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #f3f4f6;
      color: $ba-text;
    }
  }
}

.ba-body {
  padding: 16px 24px 4px;
}

.ba-global-ct-section {
  padding: 12px 14px;
  background: $ba-surface;
  border: 1px solid $ba-line;
  border-radius: 10px;

  .ba-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .ba-section-label {
    font-size: 11px;
    font-weight: 700;
    color: $ba-muted;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
  .ba-section-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .ba-link {
    border: none;
    background: none;
    padding: 0;
    font-size: 12px;
    font-weight: 600;
    color: $ba-navy;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
  .ba-link-sep {
    color: #d1d5db;
  }
  .ba-global-ct-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .ba-global-ct-loading {
    display: flex;
    gap: 4px;
    align-items: center;
    height: 32px;
  }
  .ba-global-ct-item {
    background: #fff;
    border: 1px solid $ba-line;
    border-radius: 999px;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;

    &:not(.is-all-done):hover {
      border-color: #a5b0e0;
    }
    &.is-checked {
      background: $ba-navy-soft;
      border-color: #c3c9eb;
    }
    &.is-all-done {
      opacity: 0.6;
    }
    // The checkbox fills the whole pill so the entire pill is clickable.
    .el-checkbox {
      display: flex;
      align-items: center;
      height: 32px;
      margin: 0;
      padding: 0 12px 0 10px;
    }
    .el-checkbox__label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding-left: 8px;
    }
    .ba-ct-name {
      font-size: 13px;
      font-weight: 500;
      color: $ba-text;
    }
    .ba-ct-count {
      min-width: 20px;
      padding: 0 6px;
      border-radius: 999px;
      background: #e5e7eb;
      color: #4b5563;
      font-size: 11px;
      font-weight: 600;
      line-height: 18px;
      text-align: center;
    }
    &.is-checked .ba-ct-count {
      background: $ba-navy;
      color: #fff;
    }
  }
}

.ba-flag {
  padding: 0 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &.ba-flag-sendout {
    background: $ba-blue-soft;
    color: $ba-blue;
  }
  &.ba-flag-allergy {
    background: $ba-amber-soft;
    color: $ba-amber;
  }
  &.ba-flag-done {
    background: $ba-green-soft;
    color: $ba-green;
  }
}

.ba-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 18px;
  margin: 12px 2px 10px;
  font-size: 12px;
  color: $ba-muted;

  .ba-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .ba-legend-chip {
    cursor: default;
    pointer-events: none;
  }
}

.ba-table.el-table {
  border: 1px solid $ba-line;
  border-radius: 10px;
  font-size: 13px;

  &::before {
    display: none;
  }
  th.el-table__cell {
    background: $ba-surface !important;
    color: $ba-muted;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding: 10px 0;
  }
  td.el-table__cell {
    vertical-align: top;
    padding: 12px 0;
    border-bottom: 1px solid #f0f1f4;
  }
  .el-table__row:hover > td.el-table__cell {
    background: #fafbff;
  }
  .ba-row-unselected td.el-table__cell {
    background: #fcfcfd;

    .cell > *:not(.el-checkbox) {
      opacity: 0.45;
    }
  }
}

.ba-order-id {
  font-weight: 700;
  font-size: 13px;
  color: $ba-text;
}
.ba-patient-name {
  font-size: 12px;
  color: $ba-muted;
  margin-top: 2px;
}
.ba-insurance {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-top: 8px;
  border: 1px solid #c3c9eb;
  border-radius: 6px;
  background: $ba-navy-soft;
  overflow: hidden;
  vertical-align: top;

  .ba-insurance-label {
    flex-shrink: 0;
    padding: 0 6px;
    background: $ba-navy;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.6px;
    line-height: 20px;
  }
  .ba-insurance-name {
    padding: 0 8px;
    color: $ba-navy;
    font-size: 12px;
    font-weight: 700;
    line-height: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &.is-other {
    border-color: $ba-line;
    background: #f9fafb;

    .ba-insurance-label {
      background: #4b5563;
    }
    .ba-insurance-name {
      color: #374151;
      font-weight: 600;
    }
  }
  &.is-missing {
    border-color: #f6dfb0;
    background: $ba-amber-soft;

    .ba-insurance-label {
      background: #f59e0b;
    }
    .ba-insurance-name {
      color: $ba-amber;
      font-style: italic;
      font-weight: 600;
    }
  }
}

.ba-ins-secondary {
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  margin-top: 4px;

  .ba-insurance {
    margin-top: 0;
    min-width: 0;
  }
}
.ba-insurance.ba-insurance-secondary {
  border-color: $ba-line;
  background: #f9fafb;

  .ba-insurance-label {
    background: #6b7280;
  }
  .ba-insurance-name {
    padding: 0 6px;
    color: #374151;
    font-size: 11px;
    font-weight: 600;
  }
}
.ba-ins-swap {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid $ba-navy;
  border-radius: 6px;
  background: $ba-navy;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;

  &:hover:not(:disabled) {
    background: #1e2966;
    transform: scale(1.06);
  }
  &.is-undo {
    background: #fff;
    color: $ba-navy;
    border-color: #c3c9eb;

    &:hover:not(:disabled) {
      background: $ba-navy-soft;
    }
  }
  &.is-busy {
    opacity: 0.5;
    cursor: wait;
  }
}
.ba-ins-swapped-note {
  margin-top: 3px;
  font-size: 10px;
  font-weight: 600;
  color: $ba-green;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ba-order-ct-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ba-ct-tag-wrap {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ba-ct-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  border: 1px solid transparent;
  transition: all 0.15s;

  &.is-selected {
    background: $ba-blue-soft;
    color: $ba-blue;
    border-color: #c7dcf7;
  }
  &.is-done {
    background: $ba-green-soft;
    color: $ba-green;
    border-color: #bbe5c8;
  }
  &.is-skipped {
    background: #f9fafb;
    color: $ba-faint;
    border-color: $ba-line;
  }
  &.is-error {
    background: $ba-red-soft;
    color: $ba-red;
    border-color: #f6c9c9;
  }
  .ba-ct-tag-done {
    font-weight: 700;
  }
  .ba-ct-tag-err {
    font-weight: 700;
  }
}
.ba-ct-tag-sendout,
.ba-ct-tag-allergy {
  display: inline-block;
  min-width: 14px;
  padding: 0 3px;
  border-radius: 3px;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
}
.ba-ct-tag-sendout {
  background: #3b82f6;
}
.ba-ct-tag-allergy {
  background: #f59e0b;
}
.ba-ct-error-msg {
  max-width: 200px;
  padding: 0 4px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  color: $ba-red;

  &.ba-ct-error-clickable {
    cursor: pointer;
    text-decoration: underline dotted;

    &:hover {
      color: #8f1515;
    }
  }
}
.ba-ct-error-link-hint {
  font-style: italic;
  opacity: 0.8;
}

.ba-tests-list {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 6px;
  max-height: 132px;
  overflow-y: auto;
}
.ba-test-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid #c7dcf7;
  background: $ba-blue-soft;
  color: $ba-blue;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    background: #dde9fb;
  }
  .ba-test-chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &.is-deselected {
    background: #f9fafb;
    color: $ba-faint;
    border-color: $ba-line;

    .ba-test-chip-text {
      text-decoration: line-through;
    }
    &:hover {
      background: #f3f4f6;
    }
  }
  &.is-restricted {
    background: $ba-amber-soft;
    color: $ba-amber;
    border-color: #f6dfb0;
    border-style: dashed;
    cursor: not-allowed;

    &:hover {
      background: $ba-amber-soft;
    }
  }
}
.ba-test-chip-icon {
  flex-shrink: 0;
  font-size: 8px;
  line-height: 1;
}

.ba-ack-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 160px;
  overflow-y: auto;
}
// Fixed tube-name column so the selects line up across tubes and orders.
.ba-ack-item {
  display: grid;
  grid-template-columns: 104px 128px minmax(140px, 1fr);
  align-items: center;
  column-gap: 8px;
  row-gap: 3px;
}
.ba-ack-tube-name {
  font-size: 11px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ba-ack-select,
.ba-ack-lab-select,
.ba-vendor-select {
  width: 100% !important;
}
.ba-ack-select {
  .el-input__inner {
    font-weight: 600;
  }
  &.is-received .el-input__inner {
    color: $ba-green;
    background: $ba-green-soft;
    border-color: #bbe5c8;
  }
  &.is-not-received .el-input__inner {
    color: $ba-red;
    background: $ba-red-soft;
    border-color: #f6c9c9;
  }
  &.is-changed .el-input__inner {
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.35);
  }
}
.ba-ack-by {
  grid-column: 2 / -1;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  font-size: 11px;
  color: $ba-muted;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .ba-ack-by-check {
    color: $ba-green;
    font-weight: 700;
  }
}

.ba-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 24px 18px;
  border-top: 1px solid $ba-line;
  background: #fff;
}
.ba-footer-status {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 320px;
  min-width: 0;
}
.ba-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.el-button.ba-btn {
  height: 38px;
  min-height: 38px;
  margin: 0 !important;
  padding: 0 18px;
  border-radius: 8px !important;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;

  &.ba-btn-ghost {
    background: #fff;
    border: 1px solid $ba-line;
    color: #374151;

    &:hover,
    &:focus {
      background: #f9fafb;
      border-color: #d1d5db;
      color: $ba-text;
    }
  }
  &.ba-btn-outline {
    background: #fff;
    border: 1px solid $ba-navy;
    color: $ba-navy;

    &:hover,
    &:focus {
      background: $ba-navy-soft;
      border-color: $ba-navy;
      color: $ba-navy;
    }
  }
  &.ba-btn-primary {
    background: $ba-navy;
    border: 1px solid $ba-navy;
    color: #fff;

    &:hover,
    &:focus {
      background: #1e2966;
      border-color: #1e2966;
      color: #fff;
    }
  }
  &.is-disabled,
  &.is-disabled:hover,
  &.is-disabled:focus {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ba-loading-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}
.ba-loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c0c4cc;
  display: inline-block;
  animation: ba-pulse 1.2s infinite ease-in-out;

  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.4s; }
}
@keyframes ba-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}

.ba-result-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.4;

  .ba-result-icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
  }
  &.ba-result-success {
    background: $ba-green-soft;
    color: $ba-green;
    .ba-result-icon { background: #16a34a; }
  }
  &.ba-result-error {
    background: $ba-red-soft;
    color: $ba-red;
    .ba-result-icon { background: #dc2626; }
  }
  &.ba-result-partial {
    background: $ba-amber-soft;
    color: $ba-amber;
    .ba-result-icon { background: #f59e0b; }
  }
}

.ba-empty {
  color: $ba-faint;
  font-size: 12px;
}
#all-orders-listing {
  .image-fluid {
    width: 100%;
  }

  .stickyNavbar {
    padding-bottom: 5px !important;

    .container-default {
      padding-top: 0;
    }
  }

  .listings__table {
    .name-accession {
      display: inline-block;
      overflow: hidden;
      width: 100%;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    #accession-id {
      color: #eb5757;
      cursor: pointer;
    }

    #patient-name {
      overflow: hidden;
      margin-left: 7px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.validation-button {
  display: block;
  margin-bottom: -5px;

  img {
    width: 25px;
  }
}

.reorder-radio {
  .el-radio-group {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;

    .el-radio__input .el-radio__inner {
      border-color: transparent;
      box-shadow: 0 0 0 1px #ced1d6;
    }

    .el-radio__input.is-checked .el-radio__inner {
      background-color: #0a5da6;
      border-color: #ffffff;
    }

    .el-radio__input.is-checked + .el-radio__label {
      color: #0a5da6;
    }
  }
}

.center-dropdown {
  text-align: center;

  li {
    padding: 0px 15px;
  }
}

.active-button {
  background: #273480 !important;
  color: #fff;
  font-size: 13px;
  padding: 2px 5px !important;

  &:focus,
  &:hover {
    color: #fff !important;
  }
}
.phlebio-radio {
  .el-radio-group {
    display: flex;

    .el-radio__input .el-radio__inner {
      border-color: transparent;
      box-shadow: 0 0 0 1px #ced1d6;
    }

    .el-radio__input.is-checked .el-radio__inner {
      background-color: #0a5da6;
      border-color: #ffffff;
    }

    .el-radio__input.is-checked + .el-radio__label {
      color: #0a5da6;
    }
  }
}
.phlebio-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  row-gap: 10px;
  padding-top: 0.5rem;

  .el-input__inner {
    margin-top: 5px;
    background: #ffffff;
    border: 0.719521px solid #56ccf2;
    box-sizing: border-box;
    border-radius: 2px;
    height: 35px;
  }

  .name {
    height: 30px;
    color: #000000;
  }
}
</style>
